import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuid } from 'uuid'
import isEqual from 'lodash/isEqual'
import { createChart, IChartApi, ISeriesApi, IPriceLine, LastPriceAnimationMode, LineStyle, LogicalRange, MouseEventParams, PriceScaleMode, Range, Time } from 'lightweight-charts'
import { Security } from '../../proto/generated/security'
import { SuperChartConfiguration_DateRange, SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { ChartCandle, ChartConfig, ChartLayoutOptions, ChartMode, ChartPriceScaleOptions, ChartSnapshot, PaneConfig, Scale, Series, SeriesData, SeriesGroup as SeriesGroupConfig } from './chart.typings'
import { ChartSubType, ChartType } from '../../proto/generated/chart'
import SeriesGroup from './seriesGroup'
import ChartDataManager from './chartDataManager'
import { setChartHoveredPane } from './chartHoveredPaneRevisionStore'
import { getDistanceFromSegment } from './drawingTools/drawing.helper'
import { convertToTimezone, istToUTC, TimeZone, toDayFormat, utcToIST } from '../../util/dateTime'
import { TradingCalendarData } from '../../proto/generated/trading-calendar'
import {
    BLANK_SERIES_ID,
    CHART_ALERTS_ON_CHART_DRAWING,
    CHART_ORDERS_DRAWING,
    CHART_ORDERS_MARKERS_DRAWING,
    CHART_PRICE_SCALE_MARGINS,
    ChartViewKey,
    COMPANY_EVENT_DRAWING,
    CONTRACT_SWITCH_DRAWING,
    CORPORATE_ACTION_DRAWING,
    INITIAL_CANDLE_COUNT,
    NEWS_ARTICLE_DRAWING,
    SESSION_BREAK_DRAWING,
    PIVOT_POINTS_CODE,
    PREVIOUS_CANDLES_COUNT,
    RIGHT_CANDLES_OFFSET,
    VOLUME_INDICATOR_CODE
} from '../../assets/constants'
import { isPatternIndicator } from '../../util/patternIndicators'
import {
    CalendarData,
    constructCalendarDataLookup,
    CrossHairInfo,
    DataUpdateRequestType,
    getAreaSeriesColorOptions,
    getChartTypeFromOutput,
    getEmptyTimeslots,
    getIndicatorDrawingSettings,
    getIndicatorPriceType,
    getLineWidth,
    getTimeIndex,
    getViewportCandleInfo,
    getViewportCandles,
    isSpecialChartType,
    isTransformedChartType,
    ViewportCandleInfo,
    buildComparableSecurityPanePlacements,
    getPercentageModeBaseLineSeriesOptions,
    getEffectiveAlertSettings,
    legacyComparablePlacementRecord,
    COMPARABLE_SECURITY_SOURCE_INPUT_CODE,
    getTradingSessionsForDate
} from '../../util/chart'
import { getEffectiveCurrentDayHighLowLineColor, getEffectiveHighLowLineColor, getEffectivePrevDayCloseLineColor, getEffectivePrevDayHighLowLineColor, getEffectiveTodayOpenLineColor } from '../../util/scalesAndLinesDefaults'
import {
    defaultEventsSettings,
    filterCompanyEventsByDate,
    filterCorporateActionsByDate,
    getEffectiveSessionBreakColor,
} from '../../util/eventsSettingsDefaults'
import { isIntraday } from '../../util/timeBuckets'
import { canvasLineStyleToLineStyle } from '../../util/canvasSettingsDefaults'
import { ChartLineStyle } from '../colorSelector/lineStyle.types'
import { TimeBucket, TimeBucket_TimeSeries } from '../../proto/generated/common'
import { convertExpressionToDisplayExpression, convertExpressionToEvaluationExpression } from '../../util/expression'
import { Indicator, indicator_FormatFromJSON, IndicatorOutput_OutputStyle, IndicatorOutput_OutputType } from '../../proto/generated/indicator'
import { SeriesGroupCategory } from '../../pages/home'
import IndicatorSeriesGroup, { IndicatorSeriesGroupOptions } from './indicatorSeriesGroup'
import { getCurrentAndPrevDayHighLows, getTimeWidthInSeconds, supportsDayHighLowLines, updatePrimarySecurities } from './chartHelpers'
import {
    decodeIndicatorOutputSource,
    decodeStableIndicatorOutputSource,
    encodeIndicatorOutputSource,
    findSeriesGroupIdForStableIndicatorOutputSource,
    isStableIndicatorOutputSource,
    formatIndicatorOutputSourceLabel,
    getIndicatorPriceSourceLabel,
    getIndicatorSourceDependentDisplays,
    getIndicatorSourceDependentDisplayNames,
    getIndicatorSourceDependents,
    IndicatorOutputSourceOption,
    sanitizeId,
    sortIndicatorSeriesGroupsForEvaluation,
    wouldCreateIndicatorSourceCycle
} from '../../util/indicators'
import { WebSocketContextValue } from '../webSocketConnection/WebSocket'
import {
    getBaseSecurityKeyFromComparableToken,
    getComparableSeriesGroupToken,
    getExchangeAndCodeFromKey,
    getSecurityKey
} from '../../util/security'
import {
    buildIndicatorSeriesGroupLookup,
    isAlertableDrawingType,
    partitionChartAlertsBySeriesTarget,
    type ChartAlertComparablePlacement,
    type ChartAlertSeriesPlacement
} from '../../util/alerts'
import { getColorForSymbol } from '../../util/general'
import { calculateCandlesBetweenDates, zonedTime } from '../../util/chartUtils'
import DrawingManager from './drawingManager/drawingManager'
import { ChartEventBus } from './chart.eventBus'
import { ChartEvents } from './chart.events'
import { ChartDrawingItem, DrawingDisplayType, DrawingEvents, DrawingSettingGroup, DrawingSettings, DrawingTypes } from './drawingTools/drawing.interface'
import { Theme } from '@mui/material'
import { debounce } from 'throttle-debounce'
import UndoRedoManager from '../../UndoRedoManager/UndoRedoManager'
import { ActionType } from '../UndoRedoFunctonalities/undoRedoActionTypes'
import { CompanyEventProto } from '../../proto/generated/company_event'
import { CorporateAction } from '../../proto/generated/corporate-action'
import { ChartState, IChart } from '../../../types/chart.interface'
import type DrawingBase from './drawingTools/drawing.base'
import { Order } from '../../proto/generated/order'
import { Position } from '../../proto/generated/position'
import { UserAlert } from '../../proto/generated/nexus'
import { getDrawingClassMap } from './drawingTools/drawingClassMapAccess'
import {
    getOverlayPriceScaleStyleOptions,
    OverlayPriceScale,
    OVERLAY_PRICE_SCALE_TIMESCALE_RESERVE_PX
} from './chartWrapper/overlay-price-scale'
import { PriceScaleLongPressDetector } from './priceScaleLongPressDetector'
import { CountdownPrimitive } from './customSeries/countdownPrimitive'
import { notifyChartLiveDataForSeriesGroup } from './chartSnapshotStore'

function primaryPaneHasOverlayComparable(
    primaryPane: PaneConfig | undefined,
    seriesGroups: Record<string, SeriesGroupConfig>
): boolean {
    if (!primaryPane) return false
    return primaryPane.seriesGroups.some(
        (sgid) => seriesGroups[sgid]?.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY
    )
}

/**
 * Single shared price scale: reference stays 0 so overlay indicators remain primary-relative.
 * Compare series are remapped in seriesGroup so each still starts at 0% / 100 on that scale.
 */
function isPercentageLikeScaleMode(mode: PriceScaleMode | undefined): boolean {
    return mode === PriceScaleMode.Percentage || mode === PriceScaleMode.IndexedTo100
}

/**
 * Keep overlay compares on the same scale as primary/indicators. Never opens a second price scale.
 * percentageModeReferenceSourceIndex stays 0 (indicators stay primary-relative).
 */
function applyOverlayComparableLayout(
    seriesGroups: Record<string, SeriesGroupConfig>,
    scaleOptions: ChartPriceScaleOptions,
    activeScale: Scale,
    hasOverlayComparable: boolean,
    forcePercentageWhenComparing = true
): { seriesGroups: Record<string, SeriesGroupConfig>; scaleOptions: ChartPriceScaleOptions } {
    if (!hasOverlayComparable) {
        return {
            seriesGroups,
            scaleOptions: {
                ...scaleOptions,
                percentageModeReferenceSourceIndex: 0,
            },
        }
    }

    const nextGroups = assignOverlayComparablesToScale(seriesGroups, activeScale)
    const activeMode =
        activeScale === 'LEFT' ? scaleOptions.leftScaleMode : scaleOptions.rightScaleMode
    const mode =
        forcePercentageWhenComparing && !isPercentageLikeScaleMode(activeMode)
            ? PriceScaleMode.Percentage
            : activeMode

    const nextScaleOptions: ChartPriceScaleOptions = {
        ...scaleOptions,
        percentageModeReferenceSourceIndex: 0,
    }

    if (activeScale === 'LEFT') {
        nextScaleOptions.leftScaleMode = mode
        nextScaleOptions.leftScaleVisible = true
        nextScaleOptions.rightScaleVisible = false
    } else {
        nextScaleOptions.rightScaleMode = mode
        nextScaleOptions.rightScaleVisible = true
        nextScaleOptions.leftScaleVisible = false
        nextScaleOptions.leftScaleMode = PriceScaleMode.Normal
    }

    return { seriesGroups: nextGroups, scaleOptions: nextScaleOptions }
}

function assignOverlayComparablesToScale(
    seriesGroups: Record<string, SeriesGroupConfig>,
    scale: Scale
): Record<string, SeriesGroupConfig> {
    const next = { ...seriesGroups }
    Object.entries(next).forEach(([sgId, sg]) => {
        if (sg.category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return
        next[sgId] = { ...sg, scale }
    })
    return next
}

/** One placement per pane for same indicator params (duplicate series groups share one alert overlay). */
function chartAlertSeriesGroupDedupeKey(paneId: string, scriptCode: string, inputValues: Record<string, string>): string {
    const entries = Object.entries(inputValues ?? {}).map(([k, v]) => [sanitizeId(k), String(v ?? '')] as const)
    entries.sort((a, b) => a[0].localeCompare(b[0]))
    const inputsPart = entries.map(([k, v]) => `${k}=${v}`).join('&')
    return `${paneId}\x1e${scriptCode}\x1e${inputsPart}`
}

function getOrderedComparableSeriesGroupIds(
    panes: string[],
    panesConfig: Record<string, PaneConfig>,
    seriesGroups: Record<string, SeriesGroupConfig>
): string[] {
    const ids: string[] = []
    for (const paneId of panes) {
        const pane = panesConfig[paneId]
        if (!pane) continue
        for (const sgid of pane.seriesGroups) {
            const sg = seriesGroups[sgid]
            if (sg?.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
                ids.push(sgid)
            }
        }
    }
    return ids
}

export type ChartOptions = {
    id: string
    securities: Security[]
    defaultPriceScaleId?: string
    timeBucket: SuperChartConfiguration_TimeBucket
    timeBuckets: SuperChartConfiguration_TimeBucket[]
    indicators: Indicator[]
    comparableSecurities: Security[]
    dateRange: SuperChartConfiguration_DateRange | undefined
    drawings?: ChartDrawingItem[]
    expression?: string
    expressionName?: string
    expressionToName: Map<string, string>
    chartConfig: ChartConfig
    gotoDate?: number
    viewportCandleInfo?: ViewportCandleInfo
    emptyTimeslotsSize: number
    chartType: ChartType
    layoutOptions: ChartLayoutOptions
    container: HTMLDivElement
    timezone: TimeZone
    tradingCalendarData?: TradingCalendarData
    subscriptionTag: string
    disableRealtimeUpdates?: boolean
    persistViewport?: boolean
    ws: WebSocketContextValue
    overlayPriceScale?: boolean
    restrictScroll?: boolean
    isEmbedded?: boolean
    theme: Theme
    historyManager: UndoRedoManager<any, any>
    touchEnabled: boolean
    noWhitespace?: boolean
    noOffset?: boolean
    disableDrawings?: boolean
    allowTradeFromChart?: boolean
    screenerChart?: boolean
} & ChartCallbacks

export type ChartCallbacks = {
    onDataUpdate: () => void
    subscribeSecurities: (securities: Security[], timeBucket: TimeBucket) => void
    onCrosshairMove: (event?: CrossHairInfo) => void
    onConfigUpdate: (config: ChartConfig) => void
    onDrawingsUpdate: (drawings: ChartDrawingItem[], drawingAdded?: boolean) => void
    onGotoDateUpdated: (gotoDate: number) => void
    onInitializeStart?: () => void
    onInitialized?: () => void
    onVisibleTimeRangeChange?: (timeRange: any) => void
    onViewportCandleInfoChange?: (viewportInfo: ViewportCandleInfo) => void
    onLoading?: (loading: boolean) => void
    onTimeRangeChange?: (viewportCandleInfo: ViewportCandleInfo, gotoDate?: number) => void
    //onTimeRangeChangeHistory?: (ViewportCandleInfo: ViewportCandleInfo) => void
    onMouseDown?: () => void
    onMouseEnter?: () => void
    onMouseMove?: () => void // Listener for mouse movement within the container
    onMouseLeave?: () => void
    drawingsEventListener?: (event: DrawingEvents, data: any, forced?: boolean) => void
    onReplaySelectionMove?: (time: number, price: number) => void
    onTooltipDataChange?: (show: boolean, title: string, x: number, y: number) => void
    onPriceScaleLongPress?: () => void
    onSeriesGroupError?: (seriesGroupId: string, error: string | undefined) => void
    /** Fired after indicator script finishes — active plot titles are final for current inputs. */
    onIndicatorPlotTitlesUpdated?: (seriesGroupId: string) => void
}

export default class Chart implements IChart {
    id: string
    container: HTMLDivElement
    securities: Security[]
    timeBucket: SuperChartConfiguration_TimeBucket
    indicators: Indicator[]
    comparableSecurities: Security[]
    expression: string
    expressionName?: string
    evaluationExpression: string
    evaluationContext: Record<string, Security>
    chartConfig: ChartConfig
    layoutOptions: ChartLayoutOptions
    dateRange: SuperChartConfiguration_DateRange | undefined
    chartType: ChartType
    timezone: TimeZone
    touchEnabled: boolean
    theme: Theme
    historyManager: UndoRedoManager
    silentUpdateFromHistory: boolean = false
    tradingCalendarData: TradingCalendarData | undefined
    calendarData?: CalendarData
    mode: ChartMode
    ws: WebSocketContextValue
    securityMap: Record<string, Security> = {}
    subscriptionTag: string
    disableRealtimeUpdates: boolean
    timeBuckets: SuperChartConfiguration_TimeBucket[]
    gotoDate?: number
    persistViewport = false
    drawings: ChartDrawingItem[] = []
    private initialDrawingsApplied = false
    compareMode = false
    currentCrosshairOffset = 0
    currentCrosshairTime?: number
    initialized = false
    firstLoad = true
    chartApi!: IChartApi
    noWhitespace = false
    noOffset = false
    disableDrawings = false
    screenerChart = false
    initialViewportCandleCount = 0
    viewportCandleInfo: ViewportCandleInfo = {
        barsBefore: 0,
        barsAfter: 0,
        rangeStart: 0,
        rangeEnd: 0,
        startTime: 0,
        endTime: 0,
        viewportCandles: 0
    }
    crossHairInfo?: CrossHairInfo
    previousViewPortCandleInfo: ViewportCandleInfo = {
        barsBefore: 0,
        barsAfter: 0,
        rangeStart: 0,
        rangeEnd: 0,
        startTime: 0,
        endTime: 0,
        viewportCandles: 0
    }
    /** Floored visible range start used to avoid remapping compares on every pan pixel. */
    lastComparableRemapRangeStart: number | null = null
    hoverActive = false
    overlayPriceScale = false
    /** Single overlay instance; detach before re-attaching to avoid stacked primitives breaking custom series (e.g. baseline). */
    overlayPriceScalePrimitive: OverlayPriceScale | null = null
    /** Countdown-to-bar-close label on the time axis. */
    countdownPrimitive: CountdownPrimitive | null = null
    /** Whether the exchange is in a live trading window; countdown is hidden when closed. */
    countdownLiveTradingWindow = true
    /** High/low price lines for the visible range. */
    highPriceLine: IPriceLine | null = null
    lowPriceLine: IPriceLine | null = null
    prevDayHighPriceLine: IPriceLine | null = null
    prevDayLowPriceLine: IPriceLine | null = null
    currentDayHighPriceLine: IPriceLine | null = null
    currentDayLowPriceLine: IPriceLine | null = null
    prevDayClosePriceLine: IPriceLine | null = null
    todayOpenPriceLine: IPriceLine | null = null
    prevDayClose: number | null = null
    todayOpen: number | null = null
    visibleHigh: number = -Infinity
    visibleLow: number = Infinity
    isEmbedded = false
    restrictScroll = false
    hoveredPaneId: number | undefined = undefined
    crosshairHoveredSeries?: ISeriesApi<any>
    private seriesHoverCursorActive = false
    expressionToName = new Map()
    emptyTimeslotsSize: number
    emptyTimeSlots: any[] = []
    focused = false
    containerBounds?: DOMRect
    candlesRequired = INITIAL_CANDLE_COUNT + PREVIOUS_CANDLES_COUNT
    seriesGroupRef: Record<string, SeriesGroup> = {}
    eventBus: ChartEventBus
    defaultPriceScaleId: string = 'right'
    resizeObserver: ResizeObserver
    dataManager: ChartDataManager
    primarySeriesGroup?: SeriesGroup
    drawingManager: DrawingManager
    private _mouseEntered = false
    holidays?: Set<string>
    primaryTimes: Set<Time> = new Set()
    primarySeriesClicked: boolean = false
    isDateRangeTemporary: boolean = false
    lastUpdatedCandle = 0
    notifyGoToDateToChart: boolean = false
    replayMode = false
    replayStartTime?: number

    // drawing IDs
    contractSwitchDrawingId: string = ''
    corporateActionDrawingId: string = ''
    companyEventDrawingId: string = ''
    ordersOnChartDrawingId: string = ''
    ordersMarkersOnChartDrawingId: string = ''
    chartAlertsDrawingIds: Record<string, string> = {}
    chartAlertsLastAddAttemptAt: Record<string, number> = {}
    /** Partition key for alerts staged only via {@link stageTemporaryChartAlert}; kept in `wanted` during sync so the drawing is not removed. */
    private _managerStagedChartAlertPartitionKey: string | null = null
    newsArticleDrawingId: string = ''
    sessionBreakDrawingIds: Record<string, string> = {}
    allowTradeFromChart = false
    priceScaleLongPressDetector?: PriceScaleLongPressDetector
    destroyed = false
    seriesGroupErrors: Record<string, string> = {}
    private pendingUnavailableSeriesGroupIds = new Set<string>()

    callbacks: ChartCallbacks = {
        onDataUpdate: () => { },
        onDrawingsUpdate: () => { },
        onInitialized: () => { },
        subscribeSecurities: (securities: Security[], timeBucket: TimeBucket) => { },
        onCrosshairMove: (event?: CrossHairInfo) => { },
        onConfigUpdate: (config: ChartConfig) => { },
        onVisibleTimeRangeChange: (timeRange: Range<Time> | null) => { },
        onGotoDateUpdated: (gotoDate: number) => { },
        onTooltipDataChange: (show: boolean, title: string, x: number, y: number) => { }
    }

    constructor(options: ChartOptions) {
        this.id = options.id
        this.defaultPriceScaleId = options.defaultPriceScaleId || 'right'
        this.noOffset = options.noOffset || false
        this.noWhitespace = options.noWhitespace || false
        this.disableDrawings = options.disableDrawings || false
        this.screenerChart = options.screenerChart || false
        this.historyManager = options.historyManager
        this.container = options.container
        this.securities = options.securities
        this.comparableSecurities = options.comparableSecurities
        this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            this.containerBounds = this.container.getBoundingClientRect()
        })

        this.resizeObserver.observe(this.container)
        this.generateSecurityMap()
        this.updateCompareMode()
        this.timeBucket = options.timeBucket
        this.timeBuckets = options.timeBuckets
        this.indicators = options.indicators
        this.emptyTimeslotsSize = options.emptyTimeslotsSize
        this.expression = options.expression || ''
        this.expressionToName = options.expressionToName
        this.eventBus = new ChartEventBus()
        const { expression, context } = convertExpressionToEvaluationExpression(this.expression, this.securities)
        this.evaluationExpression = expression
        this.evaluationContext = context
        this.mode = this.expression.length ? 'EXPRESSION' : 'SINGLE'
        this.chartConfig = {
            ...options.chartConfig,
            ...updatePrimarySecurities(options.chartConfig, options.securities, this.mode, this.expression, this.expressionName)
        }
        this.dateRange = options.dateRange
        this.persistViewport = options.persistViewport || false
        this.initialViewportCandleCount = Math.ceil(this.container.clientWidth / 8) // approx 10px per candle
        this.layoutOptions = options.layoutOptions
        this.chartType = options.chartType
        this.timezone = options.timezone
        this.touchEnabled = options.touchEnabled
        this.overlayPriceScale = options.overlayPriceScale || false
        this.isEmbedded = options.isEmbedded || false
        this.restrictScroll = options.restrictScroll || false
        if (options.tradingCalendarData) {
            this.updateTradingCalendarData(options.tradingCalendarData)
        }
        this.ws = options.ws
        this.subscriptionTag = options.subscriptionTag
        this.disableRealtimeUpdates = options.disableRealtimeUpdates || false
        this.callbacks.onDataUpdate = options.onDataUpdate
        this.callbacks.onGotoDateUpdated = options.onGotoDateUpdated
        this.callbacks.subscribeSecurities = options.subscribeSecurities
        this.callbacks.onCrosshairMove = options.onCrosshairMove
        this.callbacks.onConfigUpdate = options.onConfigUpdate
        this.callbacks.onDrawingsUpdate = options.onDrawingsUpdate
        this.callbacks.onVisibleTimeRangeChange = options.onVisibleTimeRangeChange
        this.callbacks.onInitialized = options.onInitialized
        this.callbacks.onInitializeStart = options.onInitializeStart
        this.theme = options.theme

        if (options.allowTradeFromChart) {
            this.allowTradeFromChart = options.allowTradeFromChart
        }

        if (options.gotoDate) {
            this.gotoDate = options.gotoDate
        }

        if (options.viewportCandleInfo) {
            this.viewportCandleInfo = options.viewportCandleInfo
            this.previousViewPortCandleInfo = options.viewportCandleInfo
        }

        if (options.onLoading) {
            this.callbacks.onLoading = options.onLoading
        }

        if (options.onViewportCandleInfoChange) {
            this.callbacks.onViewportCandleInfoChange = options.onViewportCandleInfoChange
        }

        if (options.onTimeRangeChange) {
            this.callbacks.onTimeRangeChange = options.onTimeRangeChange
        }

        if (options.onMouseDown) {
            this.callbacks.onMouseDown = options.onMouseDown
        }

        if (options.onMouseEnter) {
            this.callbacks.onMouseEnter = options.onMouseEnter
        }
        if (options.onMouseMove) {
            this.callbacks.onMouseMove = options.onMouseMove
        }
        if (options.onMouseLeave) {
            this.callbacks.onMouseLeave = options.onMouseLeave
        }

        if (options.drawingsEventListener) {
            this.callbacks.drawingsEventListener = options.drawingsEventListener
        }

        if (options.onReplaySelectionMove) {
            this.callbacks.onReplaySelectionMove = options.onReplaySelectionMove
        }

        if (options.onTooltipDataChange) {
            this.callbacks.onTooltipDataChange = options.onTooltipDataChange
        }

        if (options.onPriceScaleLongPress) {
            this.callbacks.onPriceScaleLongPress = options.onPriceScaleLongPress
        }

        if (options.onSeriesGroupError) {
            this.callbacks.onSeriesGroupError = options.onSeriesGroupError
        }

        this.drawings = cloneDeep(options.drawings || [])
        this.dataManager = new ChartDataManager({
            ...options,
            chart: this
        })

        this.drawingManager = new DrawingManager({
            chart: this
        })

        this.chartApi = createChart(this.container, {
            ...this.layoutOptions,
            height: this.container.clientHeight,
            width: this.container.clientWidth,
            autoSize: true
        })
        this.layoutOptions = cloneDeep(this.chartApi.options())
        this.initialize()
    }
    getChartId(): string {
        return this.id
    }
    getCurrentLayoutOptions() {
        return this.layoutOptions
    }
    getTimeBucket(): SuperChartConfiguration_TimeBucket {
        return this.chartConfig.timeBucket
    }
    getTimeZone(): TimeZone {
        return this.timezone
    }
    getCalendarData(): TradingCalendarData | undefined {
        return this.tradingCalendarData
    }
    getAllSecurities(): Security[] {
        return this.securities.concat(this.comparableSecurities)
    }
    async enableReplayMode() {
        this.replayMode = true
    }
    async disableReplayMode() {
        this.replayMode = false
        await this.dataManager.disableReplayMode()
    }
    async initializeReplayMode(replayStartTime: number): Promise<void> {
        this.replayStartTime = replayStartTime
        await this.dataManager.initializeReplayMode()
    }

    pushReplayData(data: Record<string, ChartCandle>): void {
        this.dataManager.pushReplayData(data)
    }

    getState(): ChartState {
        return {
            timeBucket: this.timeBucket,
            data: { ...this.dataManager.data },
            viewportCandleInfo: { ...this.viewportCandleInfo }
        }
    }

    setState(state: ChartState): void {
        this.dataManager.data = state.data
    }

    setInitialViewState(state: ChartState) {
        ; ((this.viewportCandleInfo = { ...state.viewportCandleInfo }),
            this.setSelectedDateRange({
                code: '',
                description: '',
                name: '',
                timeBucketCode: state.timeBucket.code,
                startTime: istToUTC(state.viewportCandleInfo.startTime) * 1000,
                endTime: istToUTC(state.viewportCandleInfo.endTime) * 1000
            }))
    }

    // returns true if chart is initialized from initial data
    isInitialDataAvailable() {
        return !!(this.firstLoad && this.isViewportInfoSet())
    }

    initialize() {
        this.initializeSeriesGroups(false, { deferIndicatorGroups: true }).then(() => {
            if (this.destroyed || !this.chartApi) return
            // Overlay compares share the primary scale; ref stays 0 (indicators primary-relative).
            if (this.chartConfig?.scaleOptions) {
                const primaryPane = Object.values(this.chartConfig.panesConfig ?? {}).find((p) => p.primary)
                const hasOverlayComparable = primaryPaneHasOverlayComparable(
                    primaryPane,
                    this.chartConfig.seriesGroups
                )
                if (hasOverlayComparable) {
                    const activeScale = this.getActivePriceScaleSide()
                    const { seriesGroups, scaleOptions } = applyOverlayComparableLayout(
                        { ...this.chartConfig.seriesGroups },
                        { ...this.chartConfig.scaleOptions },
                        activeScale,
                        true,
                        false
                    )
                    this.chartConfig.seriesGroups = seriesGroups
                    this.chartConfig.scaleOptions = scaleOptions
                    this.updateScaleOptions(scaleOptions, seriesGroups)
                    Object.values(this.seriesGroupRef).forEach((sg) => {
                        if (sg.config.category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return
                        if (sg.config.scale === activeScale) return
                        sg.config = { ...sg.config, scale: activeScale }
                        sg.refreshGroup(true)
                    })
                } else {
                    this.updateScaleOptions(this.chartConfig.scaleOptions)
                }
            }
            this.fetchData({ initialize: true, updateConfig: true }).finally(() => {
                if (this.destroyed || !this.chartApi) return
                this.chartApi.subscribeCrosshairMove(this.onCrosshairMove)
                this.chartApi.subscribeClick(this.onChartClick)
                this.container.addEventListener('mousedown', this.onMouseDown)
                if (this.touchEnabled) {
                    this.container.addEventListener('touchstart', this.onTouchChartHoverStart, { passive: true })
                }
                this.containerBounds = this.container.getBoundingClientRect()
                document.removeEventListener('mousemove', this.onMouseMove)
                document.addEventListener('mousemove', this.onMouseMove)
                this.setupPriceScaleLongPress()
            })
        })
    }

    setupPriceScaleLongPress() {
        if (!this.callbacks.onPriceScaleLongPress) return

        let retryCount = 0
        const maxRetries = 20

        const setupDetector = () => {
            retryCount++

            const selectors = [
                'table td:not([style*="width"])',
                'table td:first-child',
                'table td:last-child',
            ]

            let priceScaleElements: NodeListOf<Element> | null = null

            for (const selector of selectors) {
                priceScaleElements = this.container.querySelectorAll(selector)
                if (priceScaleElements.length > 0 && priceScaleElements.length < 10) {
                    break
                }
            }

            if (!priceScaleElements || priceScaleElements.length === 0) {
                if (retryCount < maxRetries) {
                    setTimeout(setupDetector, 100)
                }
                return
            }

            const filteredElements: Element[] = []
            priceScaleElements.forEach((element) => {
                const width = (element as HTMLElement).offsetWidth
                const hasWidthStyle = (element as HTMLElement).style.width

                if (width < 100 || !hasWidthStyle) {
                    filteredElements.push(element)
                }
            })

            const elementsToAttach = filteredElements.length > 0 ? filteredElements : Array.from(priceScaleElements)

            this.priceScaleLongPressDetector = new PriceScaleLongPressDetector({
                longPressDuration: 500,
                dragThreshold: 10,
                onLongPress: () => {
                    this.callbacks.onPriceScaleLongPress && this.callbacks.onPriceScaleLongPress()
                }
            })

            elementsToAttach.forEach((element) => {
                element.addEventListener('mousedown', this.priceScaleLongPressDetector!.handleMouseDown)
                element.addEventListener('touchstart', this.priceScaleLongPressDetector!.handleMouseDown, { passive: true })
            })
        }

        setupDetector()
    }

    reinitialize() {
        this.fetchData({ initialize: true, updateConfig: true })
    }

    updateTradingCalendarData(newTradingCalendardata: TradingCalendarData) {
        this.tradingCalendarData = newTradingCalendardata
        this.holidays = new Set(newTradingCalendardata.holidays.map((holiday) => holiday.date))
        this.calendarData = constructCalendarDataLookup(newTradingCalendardata, this.securities[0])
    }

    async fetchData({ initialize, updateConfig = false, fetchCompulsory = false }: { initialize: boolean; updateConfig?: boolean; fetchCompulsory?: boolean }): Promise<boolean> {
        if (initialize) {
            !this.gotoDate && !this.firstLoad && this.resetViewportCandleInfo()
            this.callbacks.onLoading && this.callbacks.onLoading(true)
        }
        if (initialize) this.silentUpdateFromHistory = true
        return this.dataManager.fetchData({ initial: initialize, updateConfig, fetchCompulsory })
    }

    async onDataLoaded(newDataFetched: boolean, updateConfig = false) {
        if (this.destroyed) return
        const requestType = this.dataManager.dataRequestType
        let primarySG = this.primarySeriesGroup
        if (!primarySG) return
        if (requestType === DataUpdateRequestType.INITIAL) {
            if (!this.initialized) {
                this.initialized = true
                this.firstLoad = false
            } else {
                this.callbacks.onInitializeStart && this.callbacks.onInitializeStart()
                this.removeEventsDrawings()
                this.removePrevDayCloseLine()
                this.removeTodayOpenLine()
                this.removeHighLowLines()
                this.removeDayHighLowLines()
            }
            !this.noWhitespace && this.updateEmptyTimeSlots()
            primarySG.updateData(newDataFetched, DataUpdateRequestType.INITIAL)
            this.attachOverlayPriceScale()
            this.attachCountdownPrimitive()
            await this.updateIndicatorSeriesGroups(newDataFetched, DataUpdateRequestType.INITIAL)
            if (this.destroyed) return
            await this.updateNonIndicatorSeriesGroups(newDataFetched, DataUpdateRequestType.INITIAL)
            if (this.destroyed) return
            // TODO: Chart Optimization check (removed from timeout)
            primarySG.unsubscribeVisibleLogicalRangeChange()
            this.unsubscribeVisibleLogicalRange()
            this.resetTimeScale()
            setTimeout(() => {
                if (this.destroyed || !this.chartApi) return
                primarySG!.subscribeVisibleLogicalRangeChange()
                this.subscribeVisibleLogicalRange()
                this.updateViewPortCandleInfo()
                this.updateHighLowLines()
                this.updateDayHighLowLines()
                this.calculateAndSetTodayOpen()
                this.primarySeriesGroup?.updateScreenerBaseline()
                this.checkIfDataRequired()
            }, 200)
            const pendingDrawings = [...this.drawings]
            if (pendingDrawings.length) {
                this.drawingManager.applyAllDrawings(pendingDrawings)
            }
            this.initialDrawingsApplied = true
            this.drawingManager.updateChartDrawings()
            if (!this.destroyed && this.chartApi) {
                this.addEventsDrawings()
            }
            setTimeout(() => {
                if (this.destroyed || !this.chartApi) return
                this.callbacks.onInitialized && this.callbacks.onInitialized()
            }, 0)
            this.dataManager.setDataLoading(false)
        } else if (requestType === DataUpdateRequestType.HISTORICAL) {
            if (newDataFetched) {
                this.primarySeriesGroup?.unsubscribeVisibleLogicalRangeChange()
                this.updateEmptyTimeSlots()
                primarySG.updateData(newDataFetched, DataUpdateRequestType.HISTORICAL)
                const dataUpdateType = isSpecialChartType(this.chartConfig.chartType) ? DataUpdateRequestType.INITIAL : DataUpdateRequestType.HISTORICAL
                await this.updateIndicatorSeriesGroups(newDataFetched, dataUpdateType)
                if (this.destroyed) return
                await this.updateNonIndicatorSeriesGroups(newDataFetched, dataUpdateType)
                if (this.destroyed) return
                this.updateViewPortCandleInfo(false)
                this.updateHighLowLines()
                this.updateDayHighLowLines()
                this.calculateAndSetTodayOpen()
                this.primarySeriesGroup?.updateScreenerBaseline()
                this.primarySeriesGroup?.subscribeVisibleLogicalRangeChange()
                this.updateEventsDrawings()
            }
            this.dataManager.setDataLoading(false)
            newDataFetched && this.checkIfDataRequired()
        } else if (requestType === DataUpdateRequestType.LIVE) {
            if (newDataFetched) {
                if (this.viewportCandleInfo.barsAfter <= 0) {
                    const data = this.getRawData()
                    const lastCandle = data[data.length - 1]
                    if (lastCandle && this.lastUpdatedCandle && (lastCandle.time as number) > this.lastUpdatedCandle) {
                        const range = this.chartApi.timeScale().getVisibleLogicalRange()
                        if (range) {
                            this.setVisibleLogicalRange(range.from + 1, range.to + 1, true)
                        }
                    }
                    if (lastCandle) {
                        this.lastUpdatedCandle = lastCandle.time as number
                    }
                }
                primarySG.updateData(newDataFetched, DataUpdateRequestType.LIVE)
                await this.updateIndicatorSeriesGroups(newDataFetched, DataUpdateRequestType.LIVE)
                if (this.destroyed) return
                await this.updateNonIndicatorSeriesGroups(newDataFetched, DataUpdateRequestType.LIVE)
                if (this.destroyed) return
                if (this.chartConfig?.scalesAndLinesSettings?.showHighLowLine || this.chartConfig?.scalesAndLinesSettings?.showHighLowLabel) {
                    this.updateHighLowLines()
                }
                const dayHlSettings = this.chartConfig?.scalesAndLinesSettings
                if (
                    dayHlSettings?.showPrevDayHighLowLine || dayHlSettings?.showPrevDayHighLowLabel
                    || dayHlSettings?.showCurrentDayHighLowLine || dayHlSettings?.showCurrentDayHighLowLabel
                ) {
                    this.updateDayHighLowLines()
                }
                // Update today open line if enabled
                if (dayHlSettings?.showTodayOpenLine || dayHlSettings?.showTodayOpenLabel) {
                    this.calculateAndSetTodayOpen()
                }
                // In replay mode, scroll viewport to show the newly added candle
                if (this.replayMode && this.chartApi) {
                    const renderedData = this.getRenderedPrimaryData()
                    const candlesLength = isTransformedChartType(this.chartConfig.chartType)
                        ? renderedData.length
                        : this.getRawData().length
                    if (candlesLength) {
                        const rightOffset = this.noOffset ? 0 : RIGHT_CANDLES_OFFSET
                        const to = candlesLength + rightOffset
                        const from = Math.max(to - this.initialViewportCandleCount - rightOffset, -1)
                        this.setVisibleLogicalRange(from, to)
                        this.updateViewPortCandleInfo()
                    }
                }
            }
        }
        updateConfig && this.callbacks.onConfigUpdate(this.chartConfig)

        this.callbacks.onDataUpdate()
    }

    setDataFetchType(type: DataUpdateRequestType) {
        this.dataManager.dataRequestType = type
    }

    recreateSeries(groupId: string, seriesId: string) {
        const seriesGroup = this.seriesGroupRef[groupId]
        if (!seriesGroup) return
        seriesGroup.recreateSeries(seriesId)

        // Reattach overlay price scale after recreation
        this.attachOverlayPriceScale()
        this.attachCountdownPrimitive()
    }

    attachOverlayPriceScale() {
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        const shouldAttach =
            (this.isEmbedded && (this.restrictScroll || this.overlayPriceScale)) ||
            (!this.isEmbedded && this.overlayPriceScale)

        if (this.overlayPriceScalePrimitive && api) {
            try {
                api.detachPrimitive(this.overlayPriceScalePrimitive)
            } catch {
                /* series may have been recreated */
            }
            this.overlayPriceScalePrimitive = null
        }

        if (!shouldAttach || !api) return

        const overlaySide = this.defaultPriceScaleId.toLowerCase() === 'left' ? 'left' : 'right'
        const primitive = new OverlayPriceScale({
            side: overlaySide,
            ...getOverlayPriceScaleStyleOptions(this.theme)
        })
        api.attachPrimitive(primitive)
        this.overlayPriceScalePrimitive = primitive
    }

    attachCountdownPrimitive() {
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (this.countdownPrimitive && api) {
            try {
                api.detachPrimitive(this.countdownPrimitive)
            } catch {
                /* series may have been recreated */
            }
            this.countdownPrimitive = null
        }
        if (!api) return
        if (isTransformedChartType(this.chartConfig.chartType)) return
        const shouldShow = this.chartConfig?.scalesAndLinesSettings?.showCountdown ?? true
        if (!shouldShow) return
        const primitive = new CountdownPrimitive(this.timeBucket, this.timezone)
        primitive.setEnabled(this.countdownLiveTradingWindow)
        api.attachPrimitive(primitive)
        this.countdownPrimitive = primitive
    }

    /** Hide the bar-close countdown when the market is closed (no candle is forming). */
    updateCountdownLiveTradingWindow(enabled: boolean) {
        this.countdownLiveTradingWindow = enabled
        this.countdownPrimitive?.setEnabled(enabled)
    }

    removeHighLowLines() {
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (api) {
            if (this.highPriceLine) { try { api.removePriceLine(this.highPriceLine) } catch { /* already removed */ } }
            if (this.lowPriceLine) { try { api.removePriceLine(this.lowPriceLine) } catch { /* already removed */ } }
        }
        this.highPriceLine = null
        this.lowPriceLine = null
        this.visibleHigh = -Infinity
        this.visibleLow = Infinity
    }

    updateHighLowLines() {
        if (this.destroyed || !this.chartApi) return
        const { showHighLowLine, showHighLowLabel } = this.chartConfig?.scalesAndLinesSettings ?? {}
        const needLines = !!(showHighLowLine || showHighLowLabel)
        if (!needLines) {
            if (this.highPriceLine || this.lowPriceLine) this.removeHighLowLines()
            return
        }
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!api) return
        const data = this.getRenderedPrimaryData() as ChartCandle[]
        if (!data.length) return
        let rangeStart = this.viewportCandleInfo.rangeStart
        let rangeEnd = this.viewportCandleInfo.rangeEnd
        const range = this.chartApi.timeScale().getVisibleLogicalRange()
        const barsInfo = range ? api.barsInLogicalRange(range) : null
        if (range && barsInfo) {
            const info = getViewportCandleInfo(range, barsInfo, data)
            rangeStart = info.rangeStart
            rangeEnd = info.rangeEnd
        } else if (!this.isViewportInfoSet()) {
            return
        }
        const start = Math.max(0, rangeStart)
        const end = Math.min(data.length - 1, rangeEnd)
        if (start > end) return
        let high = -Infinity
        let low = Infinity
        for (let i = start; i <= end; i++) {
            const c = data[i]
            if (c.high !== undefined && c.high > high) high = c.high
            if (c.low !== undefined && c.low < low) low = c.low
        }
        if (!isFinite(high) || !isFinite(low)) return
        this.visibleHigh = high
        this.visibleLow = low
        const color = getEffectiveHighLowLineColor(this.chartConfig?.scalesAndLinesSettings, this.theme)
        const lineVisible = !!showHighLowLine
        const axisLabelVisible = !!showHighLowLabel
        if (!this.highPriceLine) {
            this.highPriceLine = api.createPriceLine({ price: high, color, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'H', lineVisible, axisLabelVisible, axisLabelColor: color })
        } else {
            try {
                this.highPriceLine.applyOptions({ price: high, color, lineVisible, axisLabelVisible, axisLabelColor: color })
            } catch {
                this.highPriceLine = null
                this.highPriceLine = api.createPriceLine({ price: high, color, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'H', lineVisible, axisLabelVisible, axisLabelColor: color })
            }
        }
        if (!this.lowPriceLine) {
            this.lowPriceLine = api.createPriceLine({ price: low, color, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'L', lineVisible, axisLabelVisible, axisLabelColor: color })
        } else {
            try {
                this.lowPriceLine.applyOptions({ price: low, color, lineVisible, axisLabelVisible, axisLabelColor: color })
            } catch {
                this.lowPriceLine = null
                this.lowPriceLine = api.createPriceLine({ price: low, color, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'L', lineVisible, axisLabelVisible, axisLabelColor: color })
            }
        }
    }

    removePrevDayCloseLine() {
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (this.prevDayClosePriceLine) {
            try { api?.removePriceLine(this.prevDayClosePriceLine) } catch { /* already removed */ }
        }
        this.prevDayClosePriceLine = null
    }

    removeTodayOpenLine() {
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (this.todayOpenPriceLine) {
            try { api?.removePriceLine(this.todayOpenPriceLine) } catch { /* already removed */ }
        }
        this.todayOpenPriceLine = null
    }

    setTodayOpen(price: number | null) {
        this.todayOpen = price
        this.updateTodayOpenLine()
    }

    getTodayOpen(): number | null {
        return this.todayOpen
    }

    // Calculate today's open price from the first candle of the current trading day
    calculateAndSetTodayOpen() {
        try {
            const data = this.getRawData()
            if (!data || data.length === 0) {
                this.setTodayOpen(null)
                return
            }
            
            // Check if we have calendar data to validate session start time
            if (!this.calendarData) {
                console.log('[Today Open] No calendar data available')
                this.setTodayOpen(null)
                return
            }
            
            // Get today's start time in the local timezone
            const now = new Date()
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
            const todayStartInSeconds = Math.floor(todayStart.getTime() / 1000)
            const currentTimeInSeconds = Math.floor(now.getTime() / 1000)

            // Get trading sessions for today
            const tradingSessions = getTradingSessionsForDate(todayStartInSeconds, this.calendarData)
            
            if (!tradingSessions || tradingSessions.length === 0) {
                console.log('[Today Open] No trading sessions for today (holiday or weekend)')
                this.setTodayOpen(null)
                return
            }

            // Check if current time is after session start time
            const firstSession = tradingSessions[0]
            const sessionStartTimeInSeconds = todayStartInSeconds + firstSession.startTime
            
            if (currentTimeInSeconds < sessionStartTimeInSeconds) {
               this.setTodayOpen(null)
                return
            }

            
            let firstTodayCandle: ChartCandle | null = null
            
            for (let i = data.length - 1; i >= 0; i--) {
                const candle = data[i]
                const candleTime = typeof candle.time === 'number' ? candle.time : Number(candle.time)
                
                // Check if this candle is from today or later
                if (candleTime >= todayStartInSeconds) {
                   
                    firstTodayCandle = candle
                } else {
                    
                    break
                }
            }

            if (firstTodayCandle) {
                const candleTime = typeof firstTodayCandle.time === 'number' ? firstTodayCandle.time : Number(firstTodayCandle.time)
                this.setTodayOpen(firstTodayCandle.open)
            } else {
                this.setTodayOpen(null)
            }
        } catch (error) {
            this.setTodayOpen(null)
        }
    }

    setPrevDayClose(price: number | null) {
        this.prevDayClose = price
        this.updatePrevDayCloseLine()
    }

    getPrevDayClose(): number | null {
        return this.prevDayClose
    }

    updatePrevDayCloseLine() {
        if (this.destroyed || !this.chartApi || !this.initialized) return
        const { showPrevDayCloseLine, showPrevDayCloseLabel } = this.chartConfig?.scalesAndLinesSettings ?? {}
        const needLine = !!(showPrevDayCloseLine || showPrevDayCloseLabel)

        if (!needLine) {
            if (this.prevDayClosePriceLine) this.removePrevDayCloseLine()
            return
        }
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!api) return
        const price = this.getPrevDayClose()
        if (price === null) return
        const lineColor = getEffectivePrevDayCloseLineColor(this.chartConfig?.scalesAndLinesSettings)
        const lineVisible = !!showPrevDayCloseLine
        const axisLabelVisible = !!showPrevDayCloseLabel
        if (!this.prevDayClosePriceLine) {
            this.prevDayClosePriceLine = api.createPriceLine({ price, color: lineColor, lineWidth: 1, lineStyle: LineStyle.Dashed, title: '', lineVisible, axisLabelVisible, axisLabelColor: lineColor })
        } else {
            this.prevDayClosePriceLine.applyOptions({ price, color: lineColor, lineVisible, axisLabelVisible, axisLabelColor: lineColor })
        }
    }

    updateTodayOpenLine() {
        if (this.destroyed || !this.chartApi || !this.initialized) return
        const { showTodayOpenLine, showTodayOpenLabel } = this.chartConfig?.scalesAndLinesSettings ?? {}
        const needLine = !!(showTodayOpenLine || showTodayOpenLabel)

        if (!needLine) {
            if (this.todayOpenPriceLine) this.removeTodayOpenLine()
            return
        }
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!api) return
        const price = this.getTodayOpen()
        if (price === null) return
        const lineColor = getEffectiveTodayOpenLineColor(this.chartConfig?.scalesAndLinesSettings)
        const lineVisible = !!showTodayOpenLine
        const axisLabelVisible = !!showTodayOpenLabel
        if (!this.todayOpenPriceLine) {
            this.todayOpenPriceLine = api.createPriceLine({ price, color: lineColor, lineWidth: 1, lineStyle: LineStyle.Dashed, title: 'O', lineVisible, axisLabelVisible, axisLabelColor: lineColor })
        } else {
            this.todayOpenPriceLine.applyOptions({ price, color: lineColor, lineVisible, axisLabelVisible, axisLabelColor: lineColor, title: 'O' })
        }
    }

    removeDayHighLowLines() {
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        const lines = [this.prevDayHighPriceLine, this.prevDayLowPriceLine, this.currentDayHighPriceLine, this.currentDayLowPriceLine]
        if (api) {
            lines.forEach((line) => {
                if (line) { try { api.removePriceLine(line) } catch { /* already removed */ } }
            })
        }
        this.prevDayHighPriceLine = null
        this.prevDayLowPriceLine = null
        this.currentDayHighPriceLine = null
        this.currentDayLowPriceLine = null
    }

    private applyDayHighLowPriceLine(
        api: ISeriesApi<Time>,
        existingLine: IPriceLine | null,
        price: number,
        color: string,
        lineVisible: boolean,
        axisLabelVisible: boolean,
        title: string
    ): IPriceLine | null {
        if (!existingLine) {
            return api.createPriceLine({ price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, title, lineVisible, axisLabelVisible, axisLabelColor: color })
        }
        try {
            existingLine.applyOptions({ price, color, lineVisible, axisLabelVisible, axisLabelColor: color, title })
            return existingLine
        } catch {
            return api.createPriceLine({ price, color, lineWidth: 1, lineStyle: LineStyle.Dashed, title, lineVisible, axisLabelVisible, axisLabelColor: color })
        }
    }

    updateDayHighLowLines() {
        if (this.destroyed || !this.chartApi) return
        const settings = this.chartConfig?.scalesAndLinesSettings ?? {}
        const {
            showPrevDayHighLowLine,
            showPrevDayHighLowLabel,
            showCurrentDayHighLowLine,
            showCurrentDayHighLowLabel,
        } = settings
        const needPrevDay = !!(showPrevDayHighLowLine || showPrevDayHighLowLabel)
        const needCurrentDay = !!(showCurrentDayHighLowLine || showCurrentDayHighLowLabel)

        if (!needPrevDay && !needCurrentDay) {
            if (this.prevDayHighPriceLine || this.prevDayLowPriceLine || this.currentDayHighPriceLine || this.currentDayLowPriceLine) {
                this.removeDayHighLowLines()
            }
            return
        }

        if (!supportsDayHighLowLines(this.chartConfig.timeBucket)) {
            this.removeDayHighLowLines()
            return
        }

        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!api) return
        const data = this.getRenderedPrimaryData() as ChartCandle[]
        if (!data.length) return

        const { currentDay, prevDay } = getCurrentAndPrevDayHighLows(data, this.chartConfig.timeBucket)

        if (needPrevDay && prevDay) {
            const color = getEffectivePrevDayHighLowLineColor(settings)
            const lineVisible = !!showPrevDayHighLowLine
            const axisLabelVisible = !!showPrevDayHighLowLabel
            this.prevDayHighPriceLine = this.applyDayHighLowPriceLine(api, this.prevDayHighPriceLine, prevDay.high, color, lineVisible, axisLabelVisible, 'PDH')
            this.prevDayLowPriceLine = this.applyDayHighLowPriceLine(api, this.prevDayLowPriceLine, prevDay.low, color, lineVisible, axisLabelVisible, 'PDL')
        } else if (this.prevDayHighPriceLine || this.prevDayLowPriceLine) {
            if (this.prevDayHighPriceLine) { try { api.removePriceLine(this.prevDayHighPriceLine) } catch { /* already removed */ } }
            if (this.prevDayLowPriceLine) { try { api.removePriceLine(this.prevDayLowPriceLine) } catch { /* already removed */ } }
            this.prevDayHighPriceLine = null
            this.prevDayLowPriceLine = null
        }

        if (needCurrentDay && currentDay) {
            const color = getEffectiveCurrentDayHighLowLineColor(settings, this.theme)
            const lineVisible = !!showCurrentDayHighLowLine
            const axisLabelVisible = !!showCurrentDayHighLowLabel
            this.currentDayHighPriceLine = this.applyDayHighLowPriceLine(api, this.currentDayHighPriceLine, currentDay.high, color, lineVisible, axisLabelVisible, 'DH')
            this.currentDayLowPriceLine = this.applyDayHighLowPriceLine(api, this.currentDayLowPriceLine, currentDay.low, color, lineVisible, axisLabelVisible, 'DL')
        } else if (this.currentDayHighPriceLine || this.currentDayLowPriceLine) {
            if (this.currentDayHighPriceLine) { try { api.removePriceLine(this.currentDayHighPriceLine) } catch { /* already removed */ } }
            if (this.currentDayLowPriceLine) { try { api.removePriceLine(this.currentDayLowPriceLine) } catch { /* already removed */ } }
            this.currentDayHighPriceLine = null
            this.currentDayLowPriceLine = null
        }
    }

    /** Toggle TradingView-style last-price pulse on the primary series (e.g. when exchange session is live). */
    updatePrimaryLastPriceAnimation(enabled: boolean) {
        if (this.destroyed) return
        const api = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!api) return
        try {
            api.applyOptions({
                lastPriceAnimation: enabled ? LastPriceAnimationMode.OnDataUpdate : LastPriceAnimationMode.Disabled
            })
        } catch {
            /* Some custom series may not support last price animation */
        }
    }

    usesOverlayPriceScale(): boolean {
        return (this.isEmbedded && (this.restrictScroll || this.overlayPriceScale)) || (!this.isEmbedded && this.overlayPriceScale)
    }

    /** Extra logical bars on left or right of the visible range so the time scale clears overlay labels */
    private applyOverlayTimeScaleLogicalMargin(from: number, to: number): { from: number; to: number } {
        if (!this.chartApi || !this.usesOverlayPriceScale()) {
            return { from, to }
        }
        const barSpacing = this.chartApi.timeScale().options().barSpacing || 6
        const extraBars = Math.max(1, Math.ceil(OVERLAY_PRICE_SCALE_TIMESCALE_RESERVE_PX / barSpacing))
        if (this.defaultPriceScaleId.toLowerCase() === 'left') {
            return { from: from - extraBars, to }
        }
        return { from, to: to + extraBars }
    }

    updateViewPortCandleInfo(publish = true) {
        if (this.destroyed || !this.chartApi) return
        const primarySeries = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!primarySeries) return
        const range = this.chartApi.timeScale().getVisibleLogicalRange()

        if (!range) return
        const barsInfo = primarySeries.barsInLogicalRange(range)
        if (!barsInfo) return

        this.setViewportCandleInfo(getViewportCandleInfo(range, barsInfo, this.getRenderedPrimaryData()))
        publish && this.callbacks.onViewportCandleInfoChange && this.callbacks.onViewportCandleInfoChange(this.viewportCandleInfo)
    }

    setFocused(focused: boolean) {
        this.focused = focused
    }

    updateCrossHairMoveCallback(onCrosshairMove: (event?: CrossHairInfo) => void) {
        this.callbacks.onCrosshairMove = onCrosshairMove
    }

    private shouldDrawSessionBreaks(settings?: ChartConfig['eventsSettings']): boolean {
        if (this.screenerChart || this.id === ChartViewKey.OVERVIEW || this.id.startsWith('screener-chart-')) {
            return false
        }
        const showSessionBreaks = settings?.showSessionBreaks ?? defaultEventsSettings.showSessionBreaks
        return showSessionBreaks && isIntraday(this.timeBucket?.timeSeries)
    }

    addEventsDrawings() {
        if (this.screenerChart) return
        if (!this.disableDrawings && this.drawingManager.chart.id !== ChartViewKey.OVERVIEW) {
            this.syncEventsDrawings()
            this.addOrUpdateOrdersOnChartToolDrawing()
            this.addOrUpdateChartAlertsToolDrawing()
            this.addContractSwitchesDrawing()
        }
    }

    syncEventsDrawings() {
        if (this.disableDrawings || this.screenerChart) return

        const settings = this.chartConfig?.eventsSettings
        const showDividends = settings?.showDividends ?? defaultEventsSettings.showDividends
        const showSplits = settings?.showSplits ?? defaultEventsSettings.showSplits
        const showEarnings = settings?.showEarnings ?? defaultEventsSettings.showEarnings
        const showLatestNews = settings?.showLatestNews ?? defaultEventsSettings.showLatestNews
        const showSessionBreaks = settings?.showSessionBreaks ?? defaultEventsSettings.showSessionBreaks

        const filteredCorporateActions = filterCorporateActionsByDate(this.dataManager.corporateActionsByDate, settings)
        if ((showDividends || showSplits) && Object.keys(filteredCorporateActions).length) {
            if (!this.corporateActionDrawingId) {
                this.addCorporateActionsDrawing(filteredCorporateActions)
            } else {
                this.drawingManager.getDrawing(this.corporateActionDrawingId)?.updateSettings({ data: filteredCorporateActions })
            }
        } else if (this.corporateActionDrawingId) {
            this.drawingManager.removeDrawing(this.corporateActionDrawingId)
            this.corporateActionDrawingId = ''
        }

        const filteredCompanyEvents = filterCompanyEventsByDate(this.dataManager.companyEventsByDate, settings)
        if (showEarnings && Object.keys(filteredCompanyEvents).length) {
            if (!this.companyEventDrawingId) {
                this.addCompanyEventsDrawing(filteredCompanyEvents)
            } else {
                this.drawingManager.getDrawing(this.companyEventDrawingId)?.updateSettings({ data: filteredCompanyEvents })
            }
        } else if (this.companyEventDrawingId) {
            this.drawingManager.removeDrawing(this.companyEventDrawingId)
            this.companyEventDrawingId = ''
        }

        const totalArticles = Object.values(this.dataManager.newsArticlesByDate).reduce((sum, articles) => sum + articles.length, 0)
        if (showLatestNews && totalArticles > 0) {
            if (!this.newsArticleDrawingId) {
                this.addNewsArticlesDrawing()
            } else {
                this.drawingManager.getDrawing(this.newsArticleDrawingId)?.updateSettings({
                    data: this.dataManager.newsArticlesByDate
                })
            }
        } else if (this.newsArticleDrawingId) {
            this.drawingManager.removeDrawing(this.newsArticleDrawingId)
            this.newsArticleDrawingId = ''
        }

        const sessionBreakSettings = {
            visible: showSessionBreaks,
            color: getEffectiveSessionBreakColor(settings),
            lineStyle: settings?.sessionBreakLineStyle ?? defaultEventsSettings.sessionBreakLineStyle,
        }
        this.syncSessionBreakDrawings(sessionBreakSettings)
    }

    updateEventsDrawings() {
        if (this.disableDrawings || this.screenerChart) return

        const settings = this.chartConfig?.eventsSettings

        const corporateActionDrawing = this.drawingManager.getDrawing(this.corporateActionDrawingId)
        if (corporateActionDrawing) {
            corporateActionDrawing.updateSettings({
                data: filterCorporateActionsByDate(this.dataManager.corporateActionsByDate, settings)
            })
        }

        const contractSwitchDrawing = this.drawingManager.getDrawing(this.contractSwitchDrawingId)
        if (contractSwitchDrawing) {
            contractSwitchDrawing.updateSettings({
                data: this.dataManager.contractSwitches
            })
        }

        const companyEventDrawing = this.drawingManager.getDrawing(this.companyEventDrawingId)
        if (companyEventDrawing) {
            companyEventDrawing.updateSettings({
                data: filterCompanyEventsByDate(this.dataManager.companyEventsByDate, settings)
            })
        }

        const newsArticleDrawing = this.drawingManager.getDrawing(this.newsArticleDrawingId)
        if (newsArticleDrawing) {
            newsArticleDrawing.updateSettings({
                data: this.dataManager.newsArticlesByDate
            })
        }

        if (!this.shouldDrawSessionBreaks(settings)) {
            this.removeAllSessionBreakDrawings()
            return
        }

        const sessionBreakSettings = {
            visible: settings?.showSessionBreaks ?? defaultEventsSettings.showSessionBreaks,
            color: getEffectiveSessionBreakColor(settings),
            lineStyle: settings?.sessionBreakLineStyle ?? defaultEventsSettings.sessionBreakLineStyle,
        }
        this.syncSessionBreakDrawings(sessionBreakSettings)
    }

    updateOrdersOnChartDrawing(
        chartPositions: {
            position: Position
            stopLossOrder: Order | null
            targetOrder: Order | null
        }[],
        openOrders: {
            order: Order
            SL: Order | null
            TP: Order | null
        }[]
    ) {
        if (this.screenerChart) return
        const ordersOnChart = this.drawingManager.getDrawing(this.ordersOnChartDrawingId)
        if (ordersOnChart) {
            ordersOnChart.updateSettings({
                positions: chartPositions,
                openOrders: openOrders
            })
            ordersOnChart.updateDrawing()
        } else {
            this.dataManager.updateOrdersOnChart({
                positions: chartPositions,
                openOrders: openOrders
            })
        }
    }

    updateOrdersMarkersOnChartDrawing(orders: Record<number, Order[]>) {
        if (this.screenerChart) return
        const ordersMarkersOnChart = this.drawingManager.getDrawing(this.ordersMarkersOnChartDrawingId)
        if (ordersMarkersOnChart) {
            ordersMarkersOnChart.updateSettings({
                orders: orders
            })
            ordersMarkersOnChart.updateDrawing()
        } else {
            this.dataManager.updateOrderMarkersOnChart({
                orders: orders
            })
        }
    }

    updateChartAlertsDrawing(alerts: UserAlert[]) {
        if (this.screenerChart) return
        this.dataManager.updateChartAlertsOnChart({ alerts })
        // Refresh alert icons on user drawings (start-point badge), even if alert lines are hidden.
        this.refreshDrawingLinkedAlertIcons()
        if (!this.allowTradeFromChart || !getEffectiveAlertSettings(this.chartConfig).showAlertLines) {
            this.removeAllChartAlertDrawings()
            return
        }
        this.syncChartAlertsDrawingsFromAlerts(alerts)
    }

    /** Repaint alertable drawings so linked-alert icons appear/update when alerts change. */
    refreshDrawingLinkedAlertIcons() {
        const drawings = this.drawingManager?.drawings
        if (!drawings) return
        for (const drawing of Object.values(drawings)) {
            if (!drawing) continue
            let drawingType: string | undefined
            try {
                drawingType = drawing.getDrawingType?.() as string | undefined
            } catch {
                drawingType = (drawing.options as { drawingType?: string } | undefined)?.drawingType
            }
            if (!isAlertableDrawingType(drawingType)) continue
            drawing.primitive?.requestUpdate()
        }
    }

    getChartAlertIndicatorMatchContext() {
        return {
            seriesGroups: buildIndicatorSeriesGroupLookup(this.chartConfig.seriesGroups),
            indicators: this.indicators,
        }
    }

    getChartAlertSeriesPlacements(): ChartAlertSeriesPlacement[] {
        const seen = new Set<string>()
        const out: ChartAlertSeriesPlacement[] = []
        for (const sg of Object.values(this.seriesGroupRef)) {
            const cat = sg.config.category
            if (cat !== SeriesGroupCategory.SERIES_INDICATOR && cat !== SeriesGroupCategory.SERIES_VOLUME) continue
            const scriptCode = sg.config.scriptCode
            const inputValues = { ...sg.config.inputValues }
            const dedupeKey = chartAlertSeriesGroupDedupeKey(sg.pane.id, scriptCode, inputValues)
            if (seen.has(dedupeKey)) continue
            seen.add(dedupeKey)
            out.push({
                paneId: sg.pane.id,
                scriptCode,
                inputValues,
                series: sg.series.map((s) => ({ id: s.id, code: s.code }))
            })
        }
        return out
    }

    /** Compare-security price series (per pane) so alert lines use the correct scale and canvas. */
    getChartAlertComparablePlacements(): ChartAlertComparablePlacement[] {
        const seen = new Set<string>()
        const out: ChartAlertComparablePlacement[] = []
        for (const sg of Object.values(this.seriesGroupRef)) {
            if (sg.config.category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) continue
            const token = sg.config.securityList?.[0]
            if (!token) continue
            const baseKey = getBaseSecurityKeyFromComparableToken(token)
            const [exchange, code] = getExchangeAndCodeFromKey(baseKey)
            const seriesId = sg.series[0]?.id
            if (!seriesId) continue
            const secKey = getSecurityKey(exchange, code)
            const dedupeKey = `${sg.pane.id}\x1e${secKey}`
            if (seen.has(dedupeKey)) continue
            seen.add(dedupeKey)
            out.push({
                paneId: sg.pane.id,
                seriesId,
                exchange,
                code
            })
        }
        return out
    }

    /** Map chart config time bucket to `common.TimeBucket` for alert comparison (timeValue → value). */
    getChartTimeBucketAsCommon(): TimeBucket | undefined {
        const tb = this.timeBucket
        if (!tb || (tb.timeSeries === 0 && tb.timeValue === 0)) return undefined
        return { timeSeries: tb.timeSeries as TimeBucket_TimeSeries, value: tb.timeValue }
    }

    removeAllChartAlertDrawings() {
        for (const id of Object.values(this.chartAlertsDrawingIds)) {
            if (id) this.drawingManager.removeDrawing(id)
        }
        this.chartAlertsDrawingIds = {}
        this.chartAlertsLastAddAttemptAt = {}
        this._managerStagedChartAlertPartitionKey = null
    }

    clearManagerStagedChartAlertPartitionKey() {
        this._managerStagedChartAlertPartitionKey = null
    }

    /**
     * Clears any in-tool temporary alert draft on all chart-alert drawings (e.g. after native persist).
     */
    clearAllChartAlertsTemporaryDrafts() {
        for (const did of Object.values(this.chartAlertsDrawingIds)) {
            if (!did) continue
            const dr = this.drawingManager.getDrawing(did) as { clearTemporaryAlertForDrag?: () => void }
            dr?.clearTemporaryAlertForDrag?.()
        }
        this._managerStagedChartAlertPartitionKey = null
    }

    /**
     * Show a client-only alert line from AlertsManager / native without adding it to Redux yet.
     * Outside-tap dismiss is handled in {@link ChartAlertsOnChartTool}.
     */
    stageTemporaryChartAlert(alert: UserAlert) {
        if (this.screenerChart || this.disableDrawings) return
        if (!this.allowTradeFromChart || !getEffectiveAlertSettings(this.chartConfig).showAlertLines) {
            return
        }
        const psg = this.primarySeriesGroup
        const primary =
            psg?.pane?.id && psg.series?.[0]?.id ? { paneId: psg.pane.id, seriesId: psg.series[0].id } : undefined
        if (!primary) return
        const placements = this.getChartAlertSeriesPlacements()
        const comparablePlacements = this.getChartAlertComparablePlacements()
        const chartTb = this.getChartTimeBucketAsCommon()
        const partitions = partitionChartAlertsBySeriesTarget(
            [alert],
            primary,
            placements,
            chartTb,
            comparablePlacements,
            this.getChartAlertIndicatorMatchContext()
        )
        const entries = Array.from(partitions.entries())
        if (!entries.length) return
        const [key, bucket] = entries[0]
        let drawingId = this.chartAlertsDrawingIds[key]
        if (!drawingId) {
            drawingId = uuid().replace(/[-]*/g, '').substring(0, 10)
            this.chartAlertsDrawingIds[key] = drawingId
        }
        let chartAlertsDr = this.drawingManager.getDrawing(drawingId) as {
            setTemporaryAlertForDrag?: (a: UserAlert) => void
        } | null
        if (!chartAlertsDr) {
            const now = Date.now()
            const added = this.drawingManager.addDrawing(
                CHART_ALERTS_ON_CHART_DRAWING,
                DrawingDisplayType.TOOL,
                {
                    drawingType: CHART_ALERTS_ON_CHART_DRAWING,
                    persist: false
                },
                {},
                drawingId,
                { alerts: [] },
                bucket.paneId,
                bucket.seriesId
            )
            chartAlertsDr = added as { setTemporaryAlertForDrag?: (a: UserAlert) => void } | null
            if (added) {
                this.chartAlertsLastAddAttemptAt[key] = now
            }
        }
        this._managerStagedChartAlertPartitionKey = key
        chartAlertsDr?.setTemporaryAlertForDrag?.(alert)
    }

    syncChartAlertsDrawingsFromAlerts(alerts: UserAlert[]) {
        const psg = this.primarySeriesGroup
        const primary =
            psg?.pane?.id && psg.series?.[0]?.id ? { paneId: psg.pane.id, seriesId: psg.series[0].id } : undefined
        if (!primary) {
            this.removeAllChartAlertDrawings()
            return
        }
        const placements = this.getChartAlertSeriesPlacements()
        const comparablePlacements = this.getChartAlertComparablePlacements()
        const chartTb = this.getChartTimeBucketAsCommon()
        const partitions = partitionChartAlertsBySeriesTarget(
            alerts,
            primary,
            placements,
            chartTb,
            comparablePlacements,
            this.getChartAlertIndicatorMatchContext()
        )
        const wanted = new Set(partitions.keys())
        if (this._managerStagedChartAlertPartitionKey) {
            wanted.add(this._managerStagedChartAlertPartitionKey)
        }
        for (const key of Object.keys(this.chartAlertsDrawingIds)) {
            if (!wanted.has(key)) {
                const did = this.chartAlertsDrawingIds[key]
                if (did) this.drawingManager.removeDrawing(did)
                delete this.chartAlertsDrawingIds[key]
                delete this.chartAlertsLastAddAttemptAt[key]
            }
        }
        for (const [key, bucket] of partitions.entries()) {
            if (!bucket.alerts.length) continue
            let drawingId = this.chartAlertsDrawingIds[key]
            if (!drawingId) {
                drawingId = uuid().replace(/[-]*/g, '').substring(0, 10)
                this.chartAlertsDrawingIds[key] = drawingId
            }
            const chartAlerts = this.drawingManager.getDrawing(drawingId)
            if (chartAlerts) {
                chartAlerts.updateSettings({ alerts: bucket.alerts })
                chartAlerts.updateDrawing()
            } else {
                const now = Date.now()
                const lastAttempt = this.chartAlertsLastAddAttemptAt[key] ?? 0
                if (now - lastAttempt < 800) {
                    continue
                }
                const chartAlertsDr = this.drawingManager.addDrawing(
                    CHART_ALERTS_ON_CHART_DRAWING,
                    DrawingDisplayType.TOOL,
                    {
                        drawingType: CHART_ALERTS_ON_CHART_DRAWING,
                        persist: false
                    },
                    {},
                    drawingId,
                    { alerts: bucket.alerts },
                    bucket.paneId,
                    bucket.seriesId
                )
                if (chartAlertsDr) {
                    this.chartAlertsLastAddAttemptAt[key] = now
                }
            }
        }
    }

    removeEventsDrawings() {
        if (this.disableDrawings || this.screenerChart) return
        const corporateActionDrawing = this.drawingManager.getDrawing(this.corporateActionDrawingId)
        if (corporateActionDrawing) {
            this.drawingManager.removeDrawing(this.corporateActionDrawingId)
        }
        this.corporateActionDrawingId = ''

        const contractSwitchDrawing = this.drawingManager.getDrawing(this.contractSwitchDrawingId)
        if (contractSwitchDrawing) {
            this.drawingManager.removeDrawing(this.contractSwitchDrawingId)
        }
        this.contractSwitchDrawingId = ''

        const companyEventDrawing = this.drawingManager.getDrawing(this.companyEventDrawingId)
        if (companyEventDrawing) {
            this.drawingManager.removeDrawing(this.companyEventDrawingId)
        }
        this.companyEventDrawingId = ''

        const ordersOnChartDrawing = this.drawingManager.getDrawing(this.ordersOnChartDrawingId)
        if (ordersOnChartDrawing) {
            this.drawingManager.removeDrawing(this.ordersOnChartDrawingId)
        }
        this.ordersOnChartDrawingId = ''

        const ordersMarkersOnChartDrawing = this.drawingManager.getDrawing(this.ordersMarkersOnChartDrawingId)
        if (ordersMarkersOnChartDrawing) {
            this.drawingManager.removeDrawing(this.ordersMarkersOnChartDrawingId)
        }
        this.ordersMarkersOnChartDrawingId = ''

        this.removeAllChartAlertDrawings()

        const newsArticleDrawing = this.drawingManager.getDrawing(this.newsArticleDrawingId)
        if (newsArticleDrawing) {
            this.drawingManager.removeDrawing(this.newsArticleDrawingId)
        }
        this.newsArticleDrawingId = ''

        this.removeAllSessionBreakDrawings()
    }

    removeAllSessionBreakDrawings() {
        const idsToRemove = Object.values(this.sessionBreakDrawingIds)
        for (const id of idsToRemove) {
            if (id) {
                const drawing = this.drawingManager.getDrawing(id)
                if (drawing) {
                    try {
                        drawing.updateSettings({ visible: false })
                        if (drawing.primitive) {
                            drawing.primitive.requestUpdate()
                        }
                    } catch (e) {

                    }
                    this.drawingManager.removeDrawing(id)
                }
            }
        }
        const allDrawings = this.drawingManager.drawings

        for (const [id, drawing] of Object.entries(allDrawings)) {
            if (drawing && drawing.getDrawingType && drawing.getDrawingType() === 'sessionBreak') {

                try {
                    drawing.updateSettings({ visible: false })
                    if (drawing.primitive) {
                        drawing.primitive.requestUpdate()
                    }
                } catch (e) {

                }
                this.drawingManager.removeDrawing(id)
            }
        }

        this.sessionBreakDrawingIds = {}
    }

    syncSessionBreakDrawings(sessionBreakSettings: { visible: boolean; color: string; lineStyle: string }) {
        if (this.disableDrawings || this.screenerChart) {
            this.removeAllSessionBreakDrawings()
            return
        }
        const shouldShow = sessionBreakSettings.visible && isIntraday(this.timeBucket?.timeSeries)

        if (!shouldShow) {
            this.removeAllSessionBreakDrawings()
            return
        }

        const wantedPaneIds = new Set<string>()
        for (const paneId of this.chartConfig.panes) {
            const [seriesId] = this.getDrawingSeries(paneId)
            if (seriesId) wantedPaneIds.add(paneId)
        }

        for (const paneId of Object.keys(this.sessionBreakDrawingIds)) {
            if (!wantedPaneIds.has(paneId)) {
                const did = this.sessionBreakDrawingIds[paneId]
                if (did) this.drawingManager.removeDrawing(did)
                delete this.sessionBreakDrawingIds[paneId]
            }
        }

        for (const paneId of wantedPaneIds) {
            const [seriesId] = this.getDrawingSeries(paneId)
            if (!seriesId) continue

            let drawingId = this.sessionBreakDrawingIds[paneId]
            const existing = drawingId ? this.drawingManager.getDrawing(drawingId) : undefined
            if (existing) {
                existing.updateSettings(sessionBreakSettings)
                existing.primitive?.requestUpdate()
                continue
            }

            drawingId = uuid().replace(/[-]*/g, '').substring(0, 10)
            // Register id even when addDrawing returns null (deferred until chart initialized).
            this.sessionBreakDrawingIds[paneId] = drawingId
            this.drawingManager.addDrawing(
                SESSION_BREAK_DRAWING,
                DrawingDisplayType.TOOL,
                {
                    drawingType: SESSION_BREAK_DRAWING,
                    persist: false
                },
                {},
                drawingId,
                sessionBreakSettings,
                paneId,
                seriesId
            )
        }
    }

    addContractSwitchesDrawing() {
        const id = uuid().replace(/[-]*/g, '').substring(0, 10)
        this.drawingManager.addDrawing(
            CONTRACT_SWITCH_DRAWING,
            DrawingDisplayType.TOOL,
            {
                drawingType: CONTRACT_SWITCH_DRAWING,
                persist: false
            },
            {},
            id,
            {
                data: this.dataManager.contractSwitches
            },
            this.primarySeriesGroup?.pane.id,
            this.primarySeriesGroup?.series[0].id
        )
        this.contractSwitchDrawingId = id
    }

    addCompanyEventsDrawing(data?: Record<number, CompanyEventProto[]>) {
        const id = uuid().replace(/[-]*/g, '').substring(0, 10)
        const settings = this.chartConfig?.eventsSettings
        this.drawingManager.addDrawing(
            COMPANY_EVENT_DRAWING,
            DrawingDisplayType.TOOL,
            {
                drawingType: COMPANY_EVENT_DRAWING,
                persist: false
            },
            {},
            id,
            {
                data: data ?? filterCompanyEventsByDate(this.dataManager.companyEventsByDate, settings)
            },
            this.primarySeriesGroup?.pane.id,
            this.primarySeriesGroup?.series[0].id
        )
        this.companyEventDrawingId = id
    }

    addCorporateActionsDrawing(data?: Record<number, CorporateAction[]>) {
        const id = uuid().replace(/[-]*/g, '').substring(0, 10)
        const settings = this.chartConfig?.eventsSettings
        this.drawingManager.addDrawing(
            CORPORATE_ACTION_DRAWING,
            DrawingDisplayType.TOOL,
            {
                drawingType: CORPORATE_ACTION_DRAWING,
                persist: false
            },
            {},
            id,
            {
                data: data ?? filterCorporateActionsByDate(this.dataManager.corporateActionsByDate, settings)
            },
            this.primarySeriesGroup?.pane.id,
            this.primarySeriesGroup?.series[0].id
        )
        this.corporateActionDrawingId = id
    }

    addOrUpdateOrdersOnChartToolDrawing() {
        if (this.allowTradeFromChart) {
            if (!this.ordersMarkersOnChartDrawingId && this.chartConfig?.tradingSettings?.showOrderMarkersOnChart) {
                const idMarkers = uuid().replace(/[-]*/g, '').substring(0, 10)
                this.drawingManager.addDrawing(
                    CHART_ORDERS_MARKERS_DRAWING,
                    DrawingDisplayType.TOOL,
                    {
                        drawingType: CHART_ORDERS_MARKERS_DRAWING,
                        persist: false
                    },
                    {},
                    idMarkers,
                    this.dataManager.ordersMarkersOnChart,
                    this.primarySeriesGroup?.pane.id,
                    this.primarySeriesGroup?.series[0].id
                )
                this.ordersMarkersOnChartDrawingId = idMarkers
            } else if (this.ordersMarkersOnChartDrawingId && !this.chartConfig?.tradingSettings?.showOrderMarkersOnChart) {
                const ordersDrawing = this.drawingManager.getDrawing(this.ordersMarkersOnChartDrawingId)
                if (ordersDrawing) {
                    this.drawingManager.removeDrawing(this.ordersMarkersOnChartDrawingId)
                }
                this.ordersMarkersOnChartDrawingId = ''
            }

            if (!this.ordersOnChartDrawingId && this.chartConfig?.tradingSettings?.showTradeFromChart) {
                const id = uuid().replace(/[-]*/g, '').substring(0, 10)
                this.drawingManager.addDrawing(
                    CHART_ORDERS_DRAWING,
                    DrawingDisplayType.TOOL,
                    {
                        drawingType: CHART_ORDERS_DRAWING,
                        persist: false
                    },
                    {},
                    id,
                    this.dataManager.ordersOnChart,
                    this.primarySeriesGroup?.pane.id,
                    this.primarySeriesGroup?.series[0].id
                )
                this.ordersOnChartDrawingId = id
            } else if (this.ordersOnChartDrawingId && !this.chartConfig?.tradingSettings?.showTradeFromChart) {
                const ordersMarkerDrawing = this.drawingManager.getDrawing(this.ordersOnChartDrawingId)
                if (ordersMarkerDrawing) {
                    this.drawingManager.removeDrawing(this.ordersOnChartDrawingId)
                }
                this.ordersOnChartDrawingId = ''
            }
        }
    }

    addOrUpdateChartAlertsToolDrawing() {
        if (!this.allowTradeFromChart) return
        if (!getEffectiveAlertSettings(this.chartConfig).showAlertLines) {
            this.removeAllChartAlertDrawings()
            return
        }
        this.syncChartAlertsDrawingsFromAlerts(this.dataManager.chartAlertsOnChart.alerts)
    }

    addNewsArticlesDrawing() {
        // Check if we have actual news articles
        const totalArticles = Object.values(this.dataManager.newsArticlesByDate).reduce((sum, articles) => sum + articles.length, 0)

        // console.log('Chart: addNewsArticlesDrawing called, total articles:', totalArticles)
        // console.log('Chart: newsArticlesByDate keys:', Object.keys(this.dataManager.newsArticlesByDate))

        if (totalArticles === 0) {
            // console.log('Chart: No news articles found, not creating drawing')
            return
        }

        const id = uuid().replace(/[-]*/g, '').substring(0, 10)

        const drawingOptions = {
            drawingType: NEWS_ARTICLE_DRAWING,
            persist: false
        }

        const drawingSettings = {
            data: this.dataManager.newsArticlesByDate
        }

        const paneId = this.primarySeriesGroup?.pane.id
        const seriesId = this.primarySeriesGroup?.series[0].id

        if (!paneId || !seriesId) {
            console.log('Chart: addNewsArticlesDrawing missing paneId/seriesId', paneId, seriesId)
            return
        }

        try {
            const drawing = this.drawingManager.addDrawing(NEWS_ARTICLE_DRAWING, DrawingDisplayType.TOOL, drawingOptions, {}, id, drawingSettings, paneId, seriesId)

            if (drawing) {
                this.newsArticleDrawingId = id
            }
        } catch (error) {
            console.error('Chart: addNewsArticlesDrawing error:', error)
            // Silently handle errors
        }
    }

    addDrawing<T extends DrawingTypes>(
        type: T,
        displayType,
        options: Record<string, any>,
        settings: Partial<DrawingSettings<T>>,
        id?: string,
        initialState: Record<string, any> = {},
        paneId?: string,
        seriesId?: string,
        referenceId?: string
    ): DrawingBase<any, any, any> | null {
        return this.drawingManager.addDrawing(type, displayType, options, settings, id, initialState, paneId, seriesId, referenceId)
    }

    updateFutureCompanyEvents(events: CompanyEventProto[]) {
        this.dataManager.updateFutureCompanyEvents(events)
        const companyEventDrawing = this.drawingManager.getDrawing(this.companyEventDrawingId)
        if (companyEventDrawing) {
            companyEventDrawing.updateSettings({
                data: filterCompanyEventsByDate(this.dataManager.companyEventsByDate, this.chartConfig?.eventsSettings)
            })
        }
    }

    updateExpressionToName(expressionToName: Map<string, string>) {
        this.expressionToName = expressionToName
        if (!this.expression) return
        const newName = expressionToName.has(this.expression!) ? expressionToName.get(this.expression!)! : convertExpressionToDisplayExpression(this.expression, this.securities)
        if (!this.primarySeriesGroup || newName === this.primarySeriesGroup.config?.name) return
        const primarySeriesGroupId = this.primarySeriesGroup?.config.id
        const newSeriesGroups = { ...this.chartConfig.seriesGroups }
        newSeriesGroups[primarySeriesGroupId] = {
            ...newSeriesGroups[primarySeriesGroupId],
            name: newName
        }
        const sid = newSeriesGroups[primarySeriesGroupId].series[0]
        const newSeries = { ...this.chartConfig.series }
        newSeries[sid] = {
            ...newSeries[sid],
            name: newName
        }
        this.updateChartConfig({
            ...this.chartConfig,
            seriesGroups: newSeriesGroups,
            series: newSeries
        })
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    getPlannedViewportLogicalRange(data: ChartCandle[]): { from: number; to: number } | null {
        if (this.gotoDate) return null

        const rawData = this.getRawData()
        const useRenderedLength = isTransformedChartType(this.chartConfig.chartType)
        const candlesLength = useRenderedLength ? data.length : rawData.length
        if (!data.length || !candlesLength) return null

        const rightOffset = this.noOffset ? 0 : RIGHT_CANDLES_OFFSET
        let to = candlesLength + rightOffset
        let from = Math.max(to - this.initialViewportCandleCount - rightOffset, -1)

        if (this.dateRange) {
            const selectedDateRange = this.dateRange
            const dateRange: [Date, Date] = [new Date(selectedDateRange.startTime), new Date(selectedDateRange.endTime || Date.now())]
            const dateRangeCandleCount = Math.ceil(calculateCandlesBetweenDates(dateRange, { timeSeries: this.timeBucket.timeSeries, value: this.timeBucket.timeValue }))
            const index = Math.max(candlesLength - dateRangeCandleCount, 0)

            if (candlesLength > dateRangeCandleCount && index) {
                from = Math.max(index - rightOffset, -1)
            } else {
                from = Math.max(-3 - rightOffset, -1)
            }

            const startTimeInSeconds = utcToIST(selectedDateRange.startTime / 1000)
            const endTimeInSeconds = utcToIST((selectedDateRange.endTime || Date.now()) / 1000)

            let nearestStartIndex = 0
            for (let i = 0; i < data.length; i++) {
                const candleTime = data[i].time as number
                if (candleTime >= startTimeInSeconds) {
                    nearestStartIndex = i
                    break
                }
                nearestStartIndex = i
            }

            let nearestEndIndex = data.length - 1
            for (let i = data.length - 1; i >= 0; i--) {
                const candleTime = data[i].time as number
                if (candleTime <= endTimeInSeconds) {
                    nearestEndIndex = i
                    break
                }
                nearestEndIndex = i
            }

            if (nearestStartIndex <= nearestEndIndex) {
                from = Math.max(nearestStartIndex - rightOffset, -1)
                to = nearestEndIndex + rightOffset
            }
        }

        return { from, to }
    }

    resetTimeScale() {
        const primarySeries = this.primarySeriesGroup?.getPrimarySeriesApi()
        this.chartApi.priceScale('right').applyOptions({ autoScale: true })
        if (!primarySeries) {
            this.dataManager.setDataLoading(false)
            this.callbacks.onLoading && this.callbacks.onLoading(false)
            return
        }

        const data = this.getRenderedPrimaryData() as ChartCandle[]
        const rawData = this.getRawData()
        const useRenderedLength = isTransformedChartType(this.chartConfig.chartType)
        let candlesLength = useRenderedLength ? data.length : rawData.length
        if (!candlesLength) {
            this.dataManager.setDataLoading(false)
            this.callbacks.onLoading && this.callbacks.onLoading(false)
            return
        }

        const range = this.getPlannedViewportLogicalRange(data)

        if (this.dateRange) {
            if (this.isDateRangeTemporary) {
                this.dateRange = undefined
                this.isDateRangeTemporary = false
            }
            this.updateChartConfig({
                ...this.chartConfig
            })
        }
        if (this.gotoDate) {
            this.focusTimeInViewPort(this.gotoDate, data)
        } else if (range) {
            this.setVisibleLogicalRange(range.from, range.to)
        }

        if (this.screenerChart && this.chartConfig.chartType === ChartType.BASELINE) {
            requestAnimationFrame(() => {
                if (this.destroyed) return
                this.primarySeriesGroup?.updateScreenerBaseline()
            })
        }

        if ([TimeBucket_TimeSeries.HOUR, TimeBucket_TimeSeries.MINUTE, TimeBucket_TimeSeries.SECOND].indexOf(this.timeBucket.timeSeries) !== -1) {
            this.chartApi.timeScale().applyOptions({
                timeVisible: true,
                secondsVisible: true
            })
        } else {
            this.chartApi.timeScale().applyOptions({
                timeVisible: false,
                secondsVisible: false
            })
        }

        const lastCandle = this.getRawData().slice(-1)[0]
        if (lastCandle) {
            this.lastUpdatedCandle = lastCandle.time as number
        }

        this.dataManager.setDataLoading(false)
        this.callbacks.onLoading && this.callbacks.onLoading(false)
    }

    private findIndicatorDefinition(scriptCode: string): Indicator | undefined {
        const normalized = sanitizeId(scriptCode)
        return this.indicators.find((item) => sanitizeId(item.code) === normalized)
    }

    private isIndicatorBackedSeriesGroup(category: SeriesGroupCategory): boolean {
        return category !== SeriesGroupCategory.SERIES_SECURITY
            && category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY
    }

    markSeriesGroupUnavailable(seriesGroupId: string) {
        this.pendingUnavailableSeriesGroupIds.add(seriesGroupId)
    }

    finalizeUnavailableSeriesGroups(publishUpdate = true) {
        if (this.destroyed || !this.pendingUnavailableSeriesGroupIds.size) return

        const ids = Array.from(this.pendingUnavailableSeriesGroupIds)
        this.pendingUnavailableSeriesGroupIds.clear()
        ids.forEach((seriesGroupId) => this.pruneUnavailableIndicatorSeriesGroup(seriesGroupId))

        if (publishUpdate) {
            this.callbacks.onConfigUpdate(this.chartConfig)
        }
    }

    private pruneUnavailableIndicatorSeriesGroup(seriesGroupId: string) {
        const newConfig = {
            ...this.chartConfig,
            panes: [...this.chartConfig.panes],
            panesConfig: { ...this.chartConfig.panesConfig },
            seriesGroups: { ...this.chartConfig.seriesGroups },
            series: { ...this.chartConfig.series },
        }

        const [paneId] = newConfig.panes.filter(
            (pid) => newConfig.panesConfig[pid]?.seriesGroups.indexOf(seriesGroupId) !== -1
        )
        if (!paneId || !newConfig.panesConfig[paneId]) return

        const pane = { ...newConfig.panesConfig[paneId] }
        const sgRef = this.seriesGroupRef[seriesGroupId]

        if (sgRef) {
            try {
                sgRef.destroy()
            } catch (error) {
                console.warn(`Failed to destroy unavailable series group "${seriesGroupId}"`, error)
            }
            delete this.seriesGroupRef[seriesGroupId]
        }

        const sgConfig = newConfig.seriesGroups[seriesGroupId]
        sgConfig?.series.forEach((seriesId) => delete newConfig.series[seriesId])
        delete newConfig.seriesGroups[seriesGroupId]

        const remainingGroups = pane.seriesGroups.filter((id) => id !== seriesGroupId)
        if (remainingGroups.length === 0 && !pane.primary) {
            const paneIndex = newConfig.panes.indexOf(paneId)
            if (paneIndex >= 0) {
                newConfig.panes.splice(paneIndex, 1)
                delete newConfig.panesConfig[paneId]
                if (this.chartApi) {
                    try {
                        this.chartApi.removePane(paneIndex)
                    } catch (error) {
                        console.warn(`Failed to remove empty pane for unavailable indicator`, error)
                    }
                }
            }
        } else {
            newConfig.panesConfig[paneId] = { ...pane, seriesGroups: remainingGroups }
        }

        this.chartConfig = newConfig
    }

    private getActiveSeriesGroupIds(): Set<string> {
        const activeIds = new Set<string>()
        for (const pid of this.chartConfig.panes) {
            const pane = this.chartConfig.panesConfig[pid]
            pane?.seriesGroups?.forEach((id) => activeIds.add(id))
        }
        return activeIds
    }

    private pruneStaleSeriesGroups() {
        const activeIds = this.getActiveSeriesGroupIds()
        for (const [sgid, sg] of Object.entries({ ...this.seriesGroupRef })) {
            if (activeIds.has(sgid)) continue
            try {
                sg.destroy()
            } catch (error) {
                console.warn(`Failed to destroy stale series group "${sgid}"`, error)
            }
            delete this.seriesGroupRef[sgid]
        }
    }

    async initializeSeriesGroups(reset = false, options?: { deferIndicatorGroups?: boolean }) {
        if (this.destroyed) return
        this.pruneStaleSeriesGroups()
        const { panes, panesConfig, seriesGroups, series: seriesConfig } = this.chartConfig
        const initPromises: Promise<void>[] = []
        const deferredInitPromises: Promise<void>[] = []

        for (const [index, pid] of panes.entries()) {
            const pane = panesConfig[pid]
            if (!pane) throw new Error(`Pane: ${pid} not present`)

            for (const sgid of pane.seriesGroups) {
                const sg = seriesGroups[sgid]
                if (!sg) throw new Error(`Series Group: ${sgid} not present`)
                let seriesGroup: SeriesGroup = this.seriesGroupRef[sgid]

                const sgOptions: IndicatorSeriesGroupOptions = {
                    config: { ...sg, scale: sg.scale },
                    series: sg.series.map((sid) => {
                        return { ...seriesConfig[sid] }
                    }),
                    paneIndex: index,
                    primary: sg.primary,
                    chart: this,
                    pane: { ...pane },
                    calendarData: this.calendarData
                }
                if (seriesGroup) {
                    if (this.isIndicatorBackedSeriesGroup(sg.category) && !this.findIndicatorDefinition(sg.scriptCode)) {
                        console.warn(`Skipping unavailable indicator "${sg.scriptCode}"`)
                        this.markSeriesGroupUnavailable(sgid)
                        continue
                    }
                    seriesGroup.updateConfig(sgOptions)
                    if (reset) seriesGroup.reset()
                    continue
                }
                if (this.isIndicatorBackedSeriesGroup(sg.category) && !this.findIndicatorDefinition(sg.scriptCode)) {
                    console.warn(`Skipping unavailable indicator "${sg.scriptCode}"`)
                    this.markSeriesGroupUnavailable(sgid)
                    continue
                }
                if ([SeriesGroupCategory.SERIES_COMPARABLE_SECURITY, SeriesGroupCategory.SERIES_SECURITY].indexOf(sg.category) !== -1) {
                    seriesGroup = new SeriesGroup(sgOptions)
                } else {
                    seriesGroup = new IndicatorSeriesGroup(sgOptions)
                }

                if (sg.primary) {
                    this.primarySeriesGroup = seriesGroup
                }
                if (this.destroyed) return

                const groupToInit = seriesGroup
                this.seriesGroupRef[sgid] = groupToInit
                const deferGroup =
                    options?.deferIndicatorGroups &&
                    sg.category !== SeriesGroupCategory.SERIES_SECURITY &&
                    sg.category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY
                const initPromise = groupToInit.initialize()
                    .then(() => {
                        if (this.destroyed) return
                        if (deferGroup && this.getRawData().length) {
                            void groupToInit.updateData(true, DataUpdateRequestType.INITIAL)
                        }
                    })
                    .catch((error) => {
                        console.warn(`Failed to initialize series group "${sgid}"`, error)
                        try {
                            groupToInit.destroy()
                        } catch (destroyError) {
                            console.warn(`Failed to clean up series group "${sgid}" after init failure`, destroyError)
                        }
                        delete this.seriesGroupRef[sgid]
                        this.markSeriesGroupUnavailable(sgid)
                    })
                if (deferGroup) {
                    deferredInitPromises.push(initPromise)
                } else {
                    initPromises.push(initPromise)
                }
            }
        }

        await Promise.all(initPromises)
        if (deferredInitPromises.length) {
            await Promise.all(deferredInitPromises)
        }
        this.finalizeUnavailableSeriesGroups()
        if (
            !this.destroyed &&
            this.allowTradeFromChart &&
            getEffectiveAlertSettings(this.chartConfig).showAlertLines
        ) {
            this.syncChartAlertsDrawingsFromAlerts(this.dataManager.chartAlertsOnChart.alerts)
        }
        if (!this.destroyed && this.shouldDrawSessionBreaks(this.chartConfig?.eventsSettings)) {
            const settings = this.chartConfig?.eventsSettings
            this.syncSessionBreakDrawings({
                visible: settings?.showSessionBreaks ?? defaultEventsSettings.showSessionBreaks,
                color: getEffectiveSessionBreakColor(settings),
                lineStyle: settings?.sessionBreakLineStyle ?? defaultEventsSettings.sessionBreakLineStyle,
            })
        }
    }

    subscribeVisibleLogicalRange() {
        if (this.destroyed || !this.chartApi) return
        this.chartApi.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleRangeChange)
    }

    unsubscribeVisibleLogicalRange() {
        if (this.destroyed || !this.chartApi) return
        this.chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleRangeChange)
    }

    subscribeSecurities(securities: Security[]) {
        this.callbacks.subscribeSecurities(securities, {
            timeSeries: this.timeBucket.timeSeries,
            value: this.timeBucket.timeValue
        })
    }

    onCrosshairMove = (data: MouseEventParams, publish = true) => {
        let offset = 0
        const renderedData = this.getRenderedPrimaryData()
        setChartHoveredPane(this.id, data.paneIndex)
        this.hoveredPaneId = data.paneIndex
        this.crosshairHoveredSeries = data.hoveredSeries as ISeriesApi<any> | undefined
        this.currentCrosshairTime = data.time != null ? (data.time as number) : undefined
        if (!renderedData.length) return
        let index
        if (data.time) {
            index = getTimeIndex(renderedData, data.time as number)
            if (Number.isFinite(index)) {
                offset = renderedData.length - index
            }
        }
        if (offset < 0) offset = 0
        this.currentCrosshairOffset = offset

        if (data && data.point) {
            this.crossHairInfo = {
                point: data.point,
                time: (this.chartApi.timeScale().coordinateToTime(data.point.x) as number) || 0, //data.time as number,
                price: this.primarySeriesGroup?.getPrimarySeriesApi()?.coordinateToPrice(data.point.y) || 0
            }
        } else {
            this.crossHairInfo = undefined
        }
        this.updateSeriesHoverCursor(data)
        publish &&
            (this.hoverActive || this.touchEnabled) &&
            this.callbacks.onCrosshairMove(this.crossHairInfo)
    }

    private setSeriesHoverCursor(active: boolean) {
        if (this.seriesHoverCursorActive === active) return
        this.seriesHoverCursorActive = active
        this.container.style.cursor = active ? 'pointer' : ''
    }

    private updateSeriesHoverCursor(param: MouseEventParams) {
        if (this.screenerChart || this.drawingManager?.getActiveDrawing() || !param.point) {
            this.setSeriesHoverCursor(false)
            return
        }
        this.setSeriesHoverCursor(Boolean(this.resolveSeriesGroupIdForChartClick(param)))
    }

    onVisibleLogicalRangeChange = (viewportCandleInfo: ViewportCandleInfo) => {
        this.setViewportCandleInfo(viewportCandleInfo)
        this.callbacks.onViewportCandleInfoChange && this.callbacks.onViewportCandleInfoChange(viewportCandleInfo)
        this.updateHighLowLines()
        this.primarySeriesGroup?.updateScreenerBaseline()
        this.refreshComparablePercentageRemaps()
        !this.replayMode && this.debouncedFetchDataOnViewportChange()
    }

    /** Re-map compare series when the visible first-value base changes in % / indexed modes. */
    refreshComparablePercentageRemaps() {
        const scaleOptions = this.chartConfig?.scaleOptions
        if (!scaleOptions) return
        const activeScale = this.getActivePriceScaleSide()
        const mode = activeScale === 'LEFT' ? scaleOptions.leftScaleMode : scaleOptions.rightScaleMode
        if (!isPercentageLikeScaleMode(mode)) return

        const rangeStartFloor = Math.floor(this.viewportCandleInfo?.rangeStart ?? 0)
        if (this.lastComparableRemapRangeStart === rangeStartFloor) return
        this.lastComparableRemapRangeStart = rangeStartFloor

        Object.values(this.seriesGroupRef).forEach((sg) => {
            if (sg?.config.category !== SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) return
            sg.updateData(true)
        })
    }

    /** Debounced to avoid hammering the API while panning/zooming; high/low lines update immediately above. */
    debouncedFetchDataOnViewportChange = debounce(100, function (this: Chart) {
        if (this.destroyed || this.replayMode) return
        this.fetchData({ initialize: false })
    })

    onVisibleTimeRangeChange = (timeRange: Range<Time> | null) => {
        if (this.callbacks && this.callbacks.onVisibleTimeRangeChange) {
            this.callbacks.onVisibleTimeRangeChange(timeRange)
        }
    }
    onVisibleRangeChange = debounce(100, (range: LogicalRange | null) => {
        const seriesApi = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!range || !seriesApi) return
        const barsInfo = seriesApi.barsInLogicalRange(range)
        if (!barsInfo) return

        const viewportCandleInfo = getViewportCandleInfo(range, barsInfo, seriesApi.data() as ChartCandle[])

        this.callbacks.onTimeRangeChange && this.callbacks.onTimeRangeChange(viewportCandleInfo, this.gotoDate)
    })

    timeToCoordinate(time: number): number {
        if (this.chartApi) {
            return this.chartApi.timeScale().timeToCoordinate(time as Time) ?? NaN
        }
        return NaN
    }

    setReplayMode(mode: boolean, publish = true) {
        this.chartConfig.replayMode = mode
        publish && this.callbacks.onConfigUpdate(this.chartConfig)
    }

    getEmptyTimeslotsData(lastPointTimeInUTC: number) {
        if (!this.calendarData) return []
        const timeBucket = this.timeBucket
        const timestamps = getEmptyTimeslots(istToUTC(lastPointTimeInUTC), this.emptyTimeslotsSize, timeBucket, this.calendarData)
        return timestamps.map((t) => ({ open: 0, high: 0, low: 0, close: 0, time: zonedTime(t, this.timezone) }))
    }

    getTimeScaleHeight(): number {
        if (this.chartApi) {
            return this.chartApi.timeScale().height()
        }
        return NaN
    }

    getBarSpace(): number {
        if (this.chartApi) {
            return this.chartApi.timeScale().options().barSpacing
        }
        return 6
    }

    getPrimaryData(length = 0): ChartCandle[] {
        if (!this.primarySeriesGroup) return []
        return this.primarySeriesGroup.getPrimarySeries()?.getPrimaryData(length) ?? []
    }

    getPrimarySeriesGroup() {
        return this.primarySeriesGroup
    }

    getPrimaryPaneId() {
        return this.primarySeriesGroup?.pane.id
    }

    getPrimarySeriesId() {
        return this.primarySeriesGroup?.getPrimarySeries()?.config?.id
    }

    getData(key: string) {
        return this.chartConfig?.replayMode ? this.dataManager.replayData[key] || [] : this.dataManager.data[key] || []
    }

    getRawData() {
        return this.getData('primary')
    }

    getLatestCandles(): Record<string, ChartCandle> {
        let data: Record<string, ChartCandle> = {}
        for (let key in this.dataManager.data) {
            const candles = this.dataManager.data[key]
            if (candles.length) {
                data[key] = candles[candles.length - 1]
            }
        }
        return data
    }

    isPrevOutofData(key: string) {
        return this.dataManager.isPrevOutOfData(key)
    }

    isNextOutofData(key: string) {
        return this.dataManager.isNextOutofData(key)
    }

    getSeriesApi(paneId: string, seriesId: string): ISeriesApi<any> | null {
        const pane = this.chartConfig.panesConfig[paneId]

        if (!pane) return null
        for (let sgid of pane.seriesGroups) {
            const sg = this.seriesGroupRef[sgid]
            if (!sg) continue

            for (let srs of sg.series) {
                if (srs.id === seriesId) {
                    const cs = sg.chartSeries[seriesId]
                    return cs?.seriesApi ?? null
                }
            }
        }

        return null
    }

    getBlankSeries(): ISeriesApi<any> | void {
        const sG = this.primarySeriesGroup
        if (!sG) return
        const blankId = sG.series[1]?.id ?? BLANK_SERIES_ID
        return sG.chartSeries[blankId]?.seriesApi
    }

    getPane(id: string): PaneConfig {
        return this.chartConfig.panesConfig[id]
    }

    getPaneIdByIndex(index: number): string {
        return this.chartConfig.panes[index]
    }

    getPaneElementById(id: string) {
        if (!this.chartApi) return undefined
        let index = this.chartConfig.panes.indexOf(id)
        if (index === -1) index = 0

        return this.chartApi.getPaneElements()[index]
    }

    getDrawingSeries(paneId: string): [string, ISeriesApi<any>] | [] {
        const pane = this.chartConfig.panesConfig[paneId]

        if (!pane) return []

        if (pane.primary && this.primarySeriesGroup) {
            return [this.primarySeriesGroup.series[0].id, this.primarySeriesGroup.getPrimarySeriesApi()!]
        }

        if (!pane.seriesGroups?.length) return []

        for (let sgid of pane.seriesGroups) {
            const sg = this.seriesGroupRef[sgid]
            if (!sg) continue

            for (let srs of sg.series) {
                const api = sg.chartSeries[srs.id]?.seriesApi
                if (api && api.data().length) return [srs.id, api]
            }
        }
        const sg = this.seriesGroupRef[pane.seriesGroups[0]]
        return sg && sg.series[0] ? [sg.series[0].id, sg.chartSeries[sg.series[0].id].seriesApi] : []
    }

    /** Series + pane used for context menu / crosshair on a given pane index (primary or indicator pane). */
    getContextMenuSeriesContext(paneIndex: number): {
        series: ISeriesApi<any>
        paneId: string
        seriesId: string
        seriesGroupConfig: SeriesGroupConfig
    } | null {
        const paneId = this.getPaneIdByIndex(paneIndex)
        if (!paneId) return null
        const dr = this.getDrawingSeries(paneId) as [] | [string, ISeriesApi<any>]
        if (dr.length !== 2 || !dr[1]) return null
        const [seriesId, series] = dr
        const pane = this.getPane(paneId)
        if (!pane?.seriesGroups?.length) return null
        for (const sgid of pane.seriesGroups) {
            const sg = this.seriesGroupRef[sgid]
            if (sg?.series?.some((s) => s.id === seriesId)) {
                return { series, paneId, seriesId, seriesGroupConfig: sg.config }
            }
        }
        return null
    }

    findSeriesGroupIdForSeriesApi(series: ISeriesApi<any>): string | undefined {
        for (const sg of Object.values(this.seriesGroupRef)) {
            for (const srs of sg.series) {
                const api = sg.chartSeries[srs.id]?.seriesApi
                if (api === series) {
                    return sg.config.id
                }
            }
        }
        return undefined
    }

    findSeriesGroupIdForSeriesApiInPane(series: ISeriesApi<any>, paneIndex: number): string | undefined {
        const paneId = this.getPaneIdByIndex(paneIndex)
        if (!paneId) return undefined

        const pane = this.getPane(paneId)
        if (!pane?.seriesGroups?.length) return undefined

        for (const sgid of pane.seriesGroups) {
            const sg = this.seriesGroupRef[sgid]
            if (!sg) continue

            for (const srs of sg.series) {
                if (srs.id === BLANK_SERIES_ID) continue
                const blankSeriesId = sg.series[1]?.id ?? BLANK_SERIES_ID
                if (srs.id === blankSeriesId) continue

                const api = sg.chartSeries[srs.id]?.seriesApi
                if (api === series) {
                    return sg.config.id
                }
            }
        }
        return undefined
    }

    resolveSeriesGroupIdFromHoveredSeries(series: ISeriesApi<any>, paneIndex: number): string | undefined {
        return this.findSeriesGroupIdForSeriesApiInPane(series, paneIndex)
    }

    private getPriceFromSeriesDataPoint(data: unknown): number | undefined {
        if (!data || typeof data !== 'object') return undefined
        const point = data as Record<string, unknown>
        if (typeof point.value === 'number' && Number.isFinite(point.value)) return point.value
        if (typeof point.close === 'number' && Number.isFinite(point.close)) return point.close
        if (typeof point.high === 'number' && typeof point.low === 'number') {
            return ((point.high as number) + (point.low as number)) / 2
        }
        return undefined
    }

    private isWhitespaceSeriesData(data: unknown): boolean {
        if (!data || typeof data !== 'object') return true
        return Object.keys(data as object).length === 0
            || (data as { time?: unknown }).time === undefined
    }

    private isClickNearSeriesAtPoint(
        api: ISeriesApi<any>,
        data: unknown,
        clickX: number,
        clickY: number,
        margin: number,
        logical?: number,
    ): boolean {
        if (this.isWhitespaceSeriesData(data)) return false
        if (!data || typeof data !== 'object') return false

        const seriesType = typeof api.seriesType === 'function' ? api.seriesType() : ''

        if (seriesType === 'Candlestick' || seriesType === 'Bar') {
            const point = data as Record<string, unknown>
            if (typeof point.high !== 'number' || typeof point.low !== 'number') return false
            const highY = api.priceToCoordinate(point.high as number)
            const lowY = api.priceToCoordinate(point.low as number)
            if (highY == null || lowY == null) return false
            const top = Math.min(highY, lowY) - margin
            const bottom = Math.max(highY, lowY) + margin
            return clickY >= top && clickY <= bottom
        }

        if (seriesType === 'Histogram') {
            const point = data as Record<string, unknown>
            if (typeof point.value !== 'number' || !Number.isFinite(point.value)) return false
            const valueY = api.priceToCoordinate(point.value)
            if (valueY == null) return false
            const baselineY = api.priceToCoordinate(0)
            if (baselineY != null && Number.isFinite(baselineY)) {
                const top = Math.min(valueY, baselineY) - margin
                const bottom = Math.max(valueY, baselineY) + margin
                return clickY >= top && clickY <= bottom
            }
            return Math.abs(clickY - valueY) <= margin
        }

        return this.isClickNearLineSeries(api, clickX, clickY, margin, logical, data)
    }

    private isClickNearLineSeries(
        api: ISeriesApi<any>,
        clickX: number,
        clickY: number,
        margin: number,
        logical: number | undefined,
        fallbackData: unknown,
    ): boolean {
        const options = api.options() as { lineWidth?: number }
        const hitMargin = margin + (options.lineWidth ?? 2) / 2
        const timeScale = this.chartApi?.timeScale()
        const points = api.data() as Array<{ time: Time; value?: number }>

        if (timeScale && points.length >= 2 && logical != null && Number.isFinite(logical)) {
            const centerIdx = Math.max(0, Math.min(Math.floor(logical), points.length - 2))
            const segmentIndices = Array.from(
                new Set([centerIdx - 1, centerIdx, centerIdx + 1].filter((i) => i >= 0 && i < points.length - 1)),
            )

            for (const i of segmentIndices) {
                const p0 = points[i]
                const p1 = points[i + 1]
                if (
                    p0?.value == null || p1?.value == null
                    || !Number.isFinite(p0.value) || !Number.isFinite(p1.value)
                ) continue

                const x0 = timeScale.timeToCoordinate(p0.time)
                const x1 = timeScale.timeToCoordinate(p1.time)
                const y0 = api.priceToCoordinate(p0.value)
                const y1 = api.priceToCoordinate(p1.value)
                if (x0 == null || x1 == null || y0 == null || y1 == null) continue

                if (getDistanceFromSegment(clickX, clickY, x0, y0, x1, y1) <= hitMargin) {
                    return true
                }
            }
        }

        const point = fallbackData as Record<string, unknown>
        const price = typeof point.value === 'number' && Number.isFinite(point.value)
            ? point.value
            : this.getPriceFromSeriesDataPoint(fallbackData)
        if (price == null) return false
        const seriesY = api.priceToCoordinate(price)
        if (seriesY == null || !Number.isFinite(seriesY)) return false
        return Math.abs(seriesY - clickY) <= hitMargin
    }

    private resolveSeriesGroupIdFromSeriesData(param: MouseEventParams, paneIndex: number): string | undefined {
        const seriesData = param.seriesData
        if (!seriesData?.size || !param.point) return undefined

        const paneId = this.getPaneIdByIndex(paneIndex)
        const pane = paneId ? this.getPane(paneId) : undefined
        if (!pane?.seriesGroups?.length) return undefined

        const clickX = param.point.x
        const clickY = param.point.y
        const margin = 10 * (window.devicePixelRatio || 1)
        const logical = param.logical

        for (const sgid of pane.seriesGroups) {
            const sg = this.seriesGroupRef[sgid]
            if (!sg) continue

            for (const srs of sg.series) {
                if (srs.id === BLANK_SERIES_ID) continue
                const blankId = sg.series[1]?.id ?? BLANK_SERIES_ID
                if (srs.id === blankId) continue

                const api = sg.chartSeries[srs.id]?.seriesApi as ISeriesApi<any> | undefined
                if (!api) continue

                const data = seriesData.get(api)
                if (!data) continue

                if (this.isClickNearSeriesAtPoint(api, data, clickX, clickY, margin, logical)) {
                    return sg.config.id
                }
            }
        }

        return undefined
    }

    findSeriesGroupIdForDrawing(drawingId: string): string | undefined {
        const drawing = this.drawingManager.drawings[drawingId]
        const settingsSeriesGroupId = drawing?._settings?.seriesGroupId as string | undefined
        if (settingsSeriesGroupId) return settingsSeriesGroupId

        const patternTitle = drawing?._settings?.patternTitle as string | undefined
        if (patternTitle) {
            for (const [seriesGroupId, seriesGroup] of Object.entries(this.seriesGroupRef)) {
                if (seriesGroup.series.some((series) => series.title === patternTitle)) {
                    return seriesGroupId
                }
            }
        }

        for (const [seriesGroupId, seriesGroup] of Object.entries(this.seriesGroupRef)) {
            if (Object.values(seriesGroup.seriesDrawings).some((seriesDrawing) => seriesDrawing?.id === drawingId)) {
                return seriesGroupId
            }
        }
        return undefined
    }

    private findSeriesGroupIdForIndicatorDrawingAtPoint(x: number, y: number): string | undefined {
        for (let i = this.drawingManager.drawingsSequence.length - 1; i >= 0; i--) {
            const drawingId = this.drawingManager.drawingsSequence[i]
            const drawing = this.drawingManager.drawings[drawingId]
            if (!drawing || drawing.displayType !== DrawingDisplayType.INDICATOR) continue
            const hit = drawing._hitTest(x, y)
            if (hit) {
                return this.findSeriesGroupIdForDrawing(drawingId)
            }
        }
        return undefined
    }

    resolveSeriesGroupIdForChartClick(param: MouseEventParams): string | undefined {
        if (!param.point) return undefined

        const hoveredObjectId = param.hoveredObjectId as string | undefined
        if (hoveredObjectId) {
            const drawingId = hoveredObjectId.split('__')[0]
            const drawing = this.drawingManager.drawings[drawingId]
            if (drawing) {
                if (drawing.displayType === DrawingDisplayType.INDICATOR) {
                    return this.findSeriesGroupIdForDrawing(drawingId)
                }
                return undefined
            }
        }

        const indicatorDrawingSeriesGroupId = this.findSeriesGroupIdForIndicatorDrawingAtPoint(
            param.point.x,
            param.point.y,
        )
        if (indicatorDrawingSeriesGroupId) return indicatorDrawingSeriesGroupId

        const paneIndex = param.paneIndex ?? this.hoveredPaneId
        if (paneIndex !== undefined) {
            const hoveredSeries = (param.hoveredSeries ?? this.crosshairHoveredSeries) as ISeriesApi<any> | undefined
            if (hoveredSeries) {
                const seriesGroupId = this.resolveSeriesGroupIdFromHoveredSeries(hoveredSeries, paneIndex)
                if (seriesGroupId) return seriesGroupId
            }

            const seriesDataGroupId = this.resolveSeriesGroupIdFromSeriesData(param, paneIndex)
            if (seriesDataGroupId) return seriesDataGroupId
        }

        return undefined
    }

    onChartClick = (param: MouseEventParams) => {
        if (!param.point || this.screenerChart) return
        if (this.drawingManager.getActiveDrawing()) return

        const seriesGroupId = this.resolveSeriesGroupIdForChartClick(param)
        this.publishEvent(
            ChartEvents.SERIES_METADATA_ROW_ACTIVATED,
            seriesGroupId ? { seriesGroupId } : {},
        )
    }

    getDrawingConfig(id: string): ChartDrawingItem | void {
        return this.drawings.find((v) => v.id === id)
    }

    updatePrimaryConfig(options: Pick<ChartOptions, 'securities' | 'expression' | 'timeBucket' | 'expressionName'>) {
        const areSecuritiesSame = isEqual(this.securities, options.securities)
        const isExpressionSame = this.expression === (options.expression || '')
        if (areSecuritiesSame && isExpressionSame && isEqual(this.timeBucket, options.timeBucket)) {
            if (this.expressionName !== options.expressionName) {
                this.expressionName = options.expressionName

                const chartConfig: ChartConfig = {
                    ...this.chartConfig,
                    ...updatePrimarySecurities(this.chartConfig, options.securities, this.mode, this.expression, this.expressionName)
                }
                this.updateChartConfig(chartConfig, true).then(() => {
                    this.callbacks.onConfigUpdate(this.chartConfig)
                })
            }
            return
        }

        this.securities = options.securities
        this.generateSecurityMap()
        if (this.tradingCalendarData) {
            this.calendarData = constructCalendarDataLookup(this.tradingCalendarData, this.securities[0])
        }
        this.expression = options.expression || ''
        this.expressionName = options.expressionName
        const { expression, context } = convertExpressionToEvaluationExpression(this.expression, this.securities)
        this.evaluationExpression = expression
        this.evaluationContext = context
        this.mode = this.expression.length ? 'EXPRESSION' : this.comparableSecurities.length ? 'COMPARE' : 'SINGLE'
        const chartConfig: ChartConfig = {
            ...this.chartConfig,
            ...updatePrimarySecurities(this.chartConfig, options.securities, this.mode, this.expression, this.expressionName)
        }

        Object.keys(chartConfig.seriesGroups).forEach((sgId) => {
            chartConfig.seriesGroups[sgId].error = undefined
        })

        if (this.persistViewport && this.viewportCandleInfo.barsAfter > 0) {
            const data = this.getRenderedPrimaryData()
            if (data && data.length) {
                const viewportCandles = getViewportCandles(this.viewportCandleInfo)
                let index = Math.max(0, this.viewportCandleInfo.rangeStart + Math.ceil(viewportCandles / 2))
                this.gotoDate = data[index]?.time
                this.previousViewPortCandleInfo = this.viewportCandleInfo
                if (!isEqual(this.timeBucket, options.timeBucket)) {
                    this.previousViewPortCandleInfo = {
                        ...this.previousViewPortCandleInfo,
                        startTime: 0,
                        endTime: 0
                    }
                }
            }
        }
        chartConfig.timeBucket = options.timeBucket
        this.timeBucket = options.timeBucket
        this.updateChartConfig(chartConfig, !areSecuritiesSame || !isExpressionSame).then(() => {
            this.reinitialize()
        })
    }

    updateComparableSecurities(
        comparableSecurities: Security[],
        pushToHistory = false,
        placementSync?: Pick<ChartConfig, 'comparableSecurityPanePlacements' | 'comparableSecurityPanePlacement'>
    ) {
        if (isEqual(this.comparableSecurities, comparableSecurities)) return
        this.comparableSecurities = comparableSecurities

        this.chartConfig = {
            ...this.chartConfig,
            comparableSecurities,
            ...(placementSync?.comparableSecurityPanePlacements !== undefined
                ? { comparableSecurityPanePlacements: placementSync.comparableSecurityPanePlacements }
                : {}),
            ...(placementSync?.comparableSecurityPanePlacement !== undefined
                ? { comparableSecurityPanePlacement: placementSync.comparableSecurityPanePlacement }
                : {})
        }
        this.generateSecurityMap()
        this.updateCompareMode()

        const config = { ...this.chartConfig }
        let newSeriesGroupConfig: Record<string, SeriesGroupConfig> = { ...config.seriesGroups }
        let panes = [...config.panes]
        const newSeriesConfig: Record<string, Series> = { ...config.series }
        const newPanesConfig: Record<string, PaneConfig> = { ...config.panesConfig }
        const newScaleOptions: ChartPriceScaleOptions = {
            ...config.scaleOptions
        }

        let [primaryPane] = Object.values(newPanesConfig).filter((v) => v.primary)
        if (!primaryPane) return
        const primaryPaneId = primaryPane.id
        const existingCountByKey: Record<string, number> = {}

        Object.values(newPanesConfig).forEach((pane) => {
            pane.seriesGroups.forEach((sgid) => {
                const sg = newSeriesGroupConfig[sgid]
                if (sg?.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
                    const baseKey = getBaseSecurityKeyFromComparableToken(sg.securityList[0])
                    existingCountByKey[baseKey] = (existingCountByKey[baseKey] || 0) + 1
                }
            })
        })

        primaryPane = newPanesConfig[primaryPaneId]
        primaryPane.seriesGroups.forEach((sgid) => {
            const sgRef = this.seriesGroupRef[sgid]
            let sg = { ...newSeriesGroupConfig[sgid] }
            if (!sg || !sgRef) return

            if (sg.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
                sg.scale = 'RIGHT'
                newSeriesGroupConfig[sgid] = sg
            } else {
                sg = {
                    ...sg,
                    compareMode: true
                }
                newSeriesGroupConfig[sgid] = sg
                sgRef.config = {
                    ...sgRef.config,
                    compareMode: true
                }
            }
            if (!sgRef || sgRef.config.scriptCode === VOLUME_INDICATOR_CODE) return
        })
        const historyData: Array<{
            chartId: string
            seriesGroupId: string
            security: any
            paneId: string
        }> = []

        const placementArr = buildComparableSecurityPanePlacements(
            this.comparableSecurities,
            config.comparableSecurityPanePlacement,
            config.comparableSecurityPanePlacements
        )

        this.comparableSecurities.forEach((security, index) => {
            const secKey = getSecurityKey(security.exchange, security.code)
            if ((existingCountByKey[secKey] || 0) > 0) {
                existingCountByKey[secKey]!--
                return
            }
            const placement = placementArr[index] ?? 'overlay'
            const seriesGroupId = uuid()
            const listToken = getComparableSeriesGroupToken(secKey, seriesGroupId)
            const seriesColor = getColorForSymbol(
                `${security.symbol || ''}#${index}`,
                this.theme.palette.background.default
            )
            const seriesId = uuid()
            const series: Series = {
                id: seriesId,
                chartType: ChartType.LINE,
                chartSubType: ChartSubType.SIMPLE,
                title: security.symbol || '',
                code: security.displayName || '',
                name: security.symbol || '',
                priceFormat: 'price',
                color: seriesColor,
                thickness: 2,
                style: IndicatorOutput_OutputStyle.SOLID,
                visibility: true
            }
            const seriesGroup: SeriesGroupConfig = {
                id: seriesGroupId,
                category: SeriesGroupCategory.SERIES_COMPARABLE_SECURITY,
                name: security.symbol || '',
                primary: false,
                compareMode: true,
                scale: 'RIGHT',
                securityList: [listToken],
                scriptCode: security.code,
                series: [series.id],
                addedTime: Date.now(),
                inputValues: { [COMPARABLE_SECURITY_SOURCE_INPUT_CODE]: 'close' },
                outputValues: {
                    [series.id]: {
                        title: security.symbol || '',
                        type: IndicatorOutput_OutputType.LINE,
                        style: IndicatorOutput_OutputStyle.SOLID,
                        subType: ChartSubType.SIMPLE,
                        visibility: true,
                        thickness: 2,
                        colors: [{ title: series.id, colorCode: seriesColor }]
                    }
                }
            }
            newSeriesGroupConfig[seriesGroup.id] = seriesGroup
            newSeriesConfig[series.id] = series

            if (placement === 'new_pane') {
                const newPaneData: PaneConfig = {
                    id: uuid(),
                    seriesGroups: [seriesGroup.id],
                    primary: false
                }
                panes.push(newPaneData.id)
                newPanesConfig[newPaneData.id] = newPaneData
                historyData.push({
                    chartId: this.id,
                    seriesGroupId: seriesGroup.id,
                    security: security,
                    paneId: newPaneData.id
                })
            } else {
                const currentPrimary = newPanesConfig[primaryPaneId]
                newPanesConfig[primaryPaneId] = {
                    ...currentPrimary,
                    seriesGroups: [...currentPrimary.seriesGroups, seriesGroup.id]
                }
                historyData.push({
                    chartId: this.id,
                    seriesGroupId: seriesGroup.id,
                    security: security,
                    paneId: primaryPaneId
                })
            }
        })

        const placementArrFinal = buildComparableSecurityPanePlacements(
            this.comparableSecurities,
            config.comparableSecurityPanePlacement,
            config.comparableSecurityPanePlacements
        )

        const hasOverlayComparable = primaryPaneHasOverlayComparable(newPanesConfig[primaryPaneId], newSeriesGroupConfig)
        const activeScale = this.getActivePriceScaleSide()
        // Shared single scale: compares on active scale, reference 0 (indicators stay primary-relative).
        const layout = applyOverlayComparableLayout(
            newSeriesGroupConfig,
            newScaleOptions,
            activeScale,
            hasOverlayComparable,
            true
        )
        newSeriesGroupConfig = layout.seriesGroups
        Object.assign(newScaleOptions, layout.scaleOptions)

        this.updateScaleOptions(newScaleOptions, newSeriesGroupConfig)
        this.updateChartConfig({
            ...config,
            comparableSecurityPanePlacements: placementArrFinal,
            comparableSecurityPanePlacement: legacyComparablePlacementRecord(this.comparableSecurities, placementArrFinal),
            panes,
            panesConfig: newPanesConfig,
            seriesGroups: newSeriesGroupConfig,
            series: newSeriesConfig,
            scaleOptions: newScaleOptions
        }).then(() => {
            // Recreate so compares attach on the shared scale; data remap handles independent 0% bases.
            this.refreshAllSeriesGroups(true)
            this.callbacks.onConfigUpdate(this.chartConfig)
            void this.fetchData({ initialize: false, updateConfig: true })
        })

        if (!pushToHistory) {
            this.historyManager.push(historyData, 'Add Comparable Securities ', ActionType.ADD_COMPARE_SECURITY_INDICATOR)
        }
    }

    checkIfDataRequired() {
        if (this.destroyed || !this.chartApi) return
        this.updateViewPortCandleInfo(false)
        const { barsAfter, barsBefore } = this.viewportCandleInfo
        if (barsBefore <= 0 || (barsAfter <= 0 && !this.dataManager.updateSocketData)) {
            this.fetchData({ initialize: false })
        }
    }

    updateTouchEnabled(touchEnabled: boolean) {
        this.touchEnabled = touchEnabled
    }

    async updateChartType(chartType: ChartType) {
        this.debouncedFetchDataOnViewportChange.cancel()
        this.removeHighLowLines()
        this.removeDayHighLowLines()
        const drawings = this.drawings
        this.chartType = chartType
        const config = { ...this.chartConfig }
        config.chartType = chartType
        const newSeriesGroupConfig: Record<string, SeriesGroupConfig> = { ...config.seriesGroups }
        const newSeriesConfig: Record<string, Series> = { ...config.series }
        Object.values(newSeriesGroupConfig).forEach((sg) => {
            if (sg.primary) {
                sg.series.forEach((sid) => {
                    if (sid === BLANK_SERIES_ID) return
                    newSeriesConfig[sid] = {
                        ...newSeriesConfig[sid],
                        chartType,
                        options: {}
                    }
                })
            }
        })
        await this.updateChartConfig({
            ...config,
            series: newSeriesConfig
        })

        this.primarySeriesGroup?.refreshGroup(true, false)
        this.refreshNonPrimaryGroups()
        this.callbacks.onConfigUpdate(this.chartConfig)

        drawings.forEach((drawing) => {
            const { type, displayType, options, settings, id, state, paneId, seriesId } = drawing
            this.drawingManager.addDrawing(type, displayType, options, settings, id, state, paneId, seriesId)
        })
        this.drawingManager.updateChartDrawings()
        this.drawingManager.applyAllDrawings(drawings)
        this.drawings = drawings
        this.addEventsDrawings()
        this.attachOverlayPriceScale()
        this.updateScaleOptions(this.chartConfig.scaleOptions)
        this.removeEmptyTimeSlots()
        this.attachCountdownPrimitive()
    }
    refreshNonPrimaryGroups() {
        Object.values(this.seriesGroupRef).forEach((sg) => {
            if (sg.primary) return
            sg.refreshGroup(true)
        })
    }

    updateEmptyTimeSlots() {
        if (!this.timeBucket || isTransformedChartType(this.chartConfig.chartType)) {
            this.emptyTimeSlots = []
            return
        }
        const primaryData = this.getRawData()
        const lastPoint = primaryData.length ? (primaryData[primaryData.length - 1].time as number) : NaN
        this.emptyTimeSlots = this.getEmptyTimeslotsData(lastPoint)
    }

    removeEmptyTimeSlots() {
        this.emptyTimeSlots = []
    }

    async updateChartConfig(config: ChartConfig, reset = true) {
        this.chartConfig = { ...config }
        await this.initializeSeriesGroups(reset)
    }

    updateChartConfigSettings(config: ChartConfig) {
        this.chartConfig = { ...config }
        if (typeof config.overlayPriceScale === 'boolean') {
            this.overlayPriceScale = config.overlayPriceScale
        }
        if (this.chartConfig?.tradingSettings) {
            this.addOrUpdateOrdersOnChartToolDrawing()
        }
        if (this.chartConfig?.tradingSettings || this.chartConfig?.alertSettings) {
            this.addOrUpdateChartAlertsToolDrawing()
        }
        this.attachCountdownPrimitive()
        const alignLabels = this.chartConfig?.scalesAndLinesSettings?.alignLabels ?? true
        this.chartApi?.priceScale('right').applyOptions({ alignLabels })
        this.chartApi?.priceScale('left').applyOptions({ alignLabels })
        if (this.layoutOptions) {
            if (this.layoutOptions['rightPriceScale']) {
                (this.layoutOptions['rightPriceScale'] as any).alignLabels = alignLabels
            }
            if (this.layoutOptions['leftPriceScale']) {
                (this.layoutOptions['leftPriceScale'] as any).alignLabels = alignLabels
            }
        }
        this.updateHighLowLines()
        this.updateDayHighLowLines()
        this.updatePrevDayCloseLine()
        this.updateTodayOpenLine()
        this.syncEventsDrawings()
        this.updateSymbolSeriesSettings()
        this.updateIndicatorSeriesSettings()
    }

    updateSymbolSeriesSettings() {
        if (this.destroyed) return
        const primarySg = this.primarySeriesGroup
        if (primarySg) {
            primarySg.refreshGroup(false)
        }
    }

    updateIndicatorSeriesSettings() {
        if (this.destroyed) return
        Object.values(this.seriesGroupRef).forEach((sg) => {
            if (!(sg instanceof IndicatorSeriesGroup)) return
            // Drawing indicator groups have no chartSeries to refresh (primitives only).
            if (sg.config.category === SeriesGroupCategory.SERIES_DRAWING) return
            sg.refreshGroup(false)
        })
    }

    applyPercentageBaseLineSeriesOptions() {
        Object.values(this.seriesGroupRef).forEach((sg) => {
            if (!sg) return
            const blankId = sg.series[1]?.id ?? BLANK_SERIES_ID
            const baseLineOptions = getPercentageModeBaseLineSeriesOptions(
                this.getPriceScaleModeForScale(sg.config.scale),
                this.theme
            )
            sg.series.forEach((series) => {
                if (series.id === blankId) return
                const chartSeries = sg.chartSeries[series.id]
                chartSeries?.seriesApi?.applyOptions(baseLineOptions)
            })
        })
    }

    private getPriceScaleModeForScale(scale: Scale): PriceScaleMode {
        const scaleOptions = this.chartConfig.scaleOptions
        if (!scaleOptions) return PriceScaleMode.Normal
        return scale === 'LEFT' ? scaleOptions.leftScaleMode : scaleOptions.rightScaleMode
    }

    updateLayoutOptions(layoutOptions: ChartLayoutOptions) {
        this.chartApi.applyOptions(layoutOptions)
        this.layoutOptions = this.chartApi.options()
        this.applyPercentageBaseLineSeriesOptions()

        Object.values(this.chartConfig?.seriesGroups ?? {}).forEach((sg) => {
            if (sg.category === SeriesGroupCategory.SERIES_DRAWING) {
                const sgRef = this.seriesGroupRef[sg.id]
                if (sgRef && sgRef.getDrawing()) {
                    sgRef.getDrawing()!.updateSettings({
                        textColor: {
                            opacity: 1,
                            color: this.layoutOptions.layout?.textColor ?? '#000000'
                        }
                    })
                }
            }
        })
    }

    /** Which side the main price scale is on (used when assigning scale to new series). */
    getActivePriceScaleSide(): Scale {
        const opts = this.chartConfig?.scaleOptions
        if (opts?.leftScaleVisible && !opts?.rightScaleVisible) {
            return 'LEFT'
        }
        if (!opts?.leftScaleVisible && opts?.rightScaleVisible) {
            return 'RIGHT'
        }
        return this.defaultPriceScaleId?.toLowerCase() === 'left' ? 'LEFT' : 'RIGHT'
    }

    getIndicatorSeriesScale(indicator: Indicator, _addPane: boolean): Scale {
        return this.getActivePriceScaleSide()
    }

    updatePriceScaleMode(scaleMode: PriceScaleMode, refresh: boolean = true) {
        const primaryPane = Object.values(this.chartConfig.panesConfig ?? {}).find((p) => p.primary)
        const hasOverlayComparable = primaryPaneHasOverlayComparable(
            primaryPane,
            this.chartConfig.seriesGroups
        )
        const activeScale = this.getActivePriceScaleSide()
        const scaleOptions: ChartPriceScaleOptions = {
            ...this.chartConfig.scaleOptions,
            percentageModeReferenceSourceIndex: 0,
        }

        if (scaleOptions.leftScaleVisible) {
            scaleOptions.leftScaleMode = scaleMode
        }
        if (scaleOptions.rightScaleVisible) {
            scaleOptions.rightScaleMode = scaleMode
        }

        const layout = applyOverlayComparableLayout(
            { ...this.chartConfig.seriesGroups },
            scaleOptions,
            activeScale,
            hasOverlayComparable,
            false
        )
        // Apply the user-selected mode on the active (shared) scale.
        if (activeScale === 'LEFT') {
            layout.scaleOptions.leftScaleMode = scaleMode
        } else {
            layout.scaleOptions.rightScaleMode = scaleMode
        }

        this.updateScaleOptions(layout.scaleOptions, layout.seriesGroups)
        this.updateChartConfig({
            ...this.chartConfig,
            scaleOptions: layout.scaleOptions,
            seriesGroups: layout.seriesGroups,
        }).then(() => {
            if (refresh || hasOverlayComparable) {
                this.refreshAllSeriesGroups(!!hasOverlayComparable)
            } else {
                this.applyPercentageBaseLineSeriesOptions()
            }
            this.callbacks.onConfigUpdate(this.chartConfig)
        })
    }

    updateInvertScale(invert: boolean) {
        const side = this.defaultPriceScaleId.toLowerCase() === 'left' ? 'left' : 'right'
        this?.chartApi?.priceScale(side).applyOptions({ invertScale: invert })
        const newConfig = { ...this.chartConfig }
        newConfig.scaleInvertOption = {
            ...newConfig.scaleInvertOption,
            invertScaleMode: invert
        }
        this.updateChartConfig(newConfig).then(() => {
            this.callbacks.onConfigUpdate(this.chartConfig)
        })
    }

    movePrimaryPriceScale() {
        const scaleOptions: ChartPriceScaleOptions = { ...this.chartConfig.scaleOptions }
        const activeScale = this.getActivePriceScaleSide()
        const targetScale: Scale = activeScale === 'RIGHT' ? 'LEFT' : 'RIGHT'
        const sourceMode = activeScale === 'RIGHT' ? scaleOptions.rightScaleMode : scaleOptions.leftScaleMode
        const invert = this.chartConfig.scaleInvertOption?.invertScaleMode ?? false

        const newSeriesGroups: Record<string, SeriesGroupConfig> = {}
        Object.entries(this.chartConfig.seriesGroups).forEach(([sgId, sg]) => {
            if (sg.category === SeriesGroupCategory.SERIES_VOLUME) {
                newSeriesGroups[sgId] = { ...sg }
            } else {
                // Primary, indicators, and compares all share the single active scale.
                newSeriesGroups[sgId] = { ...sg, scale: targetScale }
            }
        })

        if (targetScale === 'LEFT') {
            scaleOptions.leftScaleVisible = true
            scaleOptions.rightScaleVisible = false
            scaleOptions.leftScaleMode = sourceMode
        } else {
            scaleOptions.rightScaleVisible = true
            scaleOptions.leftScaleVisible = false
            scaleOptions.rightScaleMode = sourceMode
        }
        scaleOptions.percentageModeReferenceSourceIndex = 0

        this.defaultPriceScaleId = targetScale === 'LEFT' ? 'left' : 'right'

        this.chartApi?.priceScale('left').applyOptions({ invertScale: targetScale === 'LEFT' ? invert : false })
        this.chartApi?.priceScale('right').applyOptions({ invertScale: targetScale === 'RIGHT' ? invert : false })

        const newConfig = {
            ...this.chartConfig,
            scaleOptions,
            seriesGroups: newSeriesGroups,
        }
        this.updateScaleOptions(scaleOptions, newSeriesGroups)
        this.updateChartConfig(newConfig).then(() => {
            this.refreshAllSeriesGroups(true)
            this.attachOverlayPriceScale()
            this.callbacks.onConfigUpdate(this.chartConfig)
        })
    }

    updateScaleOptions(
        scaleOptions: ChartPriceScaleOptions,
        seriesGroups: Record<string, SeriesGroupConfig> = this.chartConfig?.seriesGroups
    ) {
        void seriesGroups
        const configuredIndex = scaleOptions.percentageModeReferenceSourceIndex ?? 0
        const newScaleOptions: ChartLayoutOptions = {
            rightPriceScale: {
                visible: scaleOptions.rightScaleVisible,
                mode: scaleOptions.rightScaleMode,
                percentageModeReferenceSourceIndex: configuredIndex,
                borderVisible: false,
                scaleMargins: CHART_PRICE_SCALE_MARGINS
            },
            leftPriceScale: {
                visible: scaleOptions.leftScaleVisible,
                mode: scaleOptions.leftScaleMode,
                percentageModeReferenceSourceIndex: configuredIndex,
                borderVisible: false,
                scaleMargins: CHART_PRICE_SCALE_MARGINS
            }
        }
        this.chartApi && this.chartApi.applyOptions(newScaleOptions)
    }

    // updateLeftScaleGroup() {
    //     if (this.compareMode && this.comparableSecurities.length === 1 && [PriceScaleMode.Normal, PriceScaleMode.Logarithmic].includes(this.chartConfig.scaleOptions.rightScaleMode)) {
    //         this.chartConfig.leftScaleGroup = Object.values(this.chartConfig.seriesGroups).find((sg) => sg.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY)?.id ?? ''
    //     } else {
    //         this.chartConfig.leftScaleGroup = ''
    //     }
    // }

    updateCompareMode() {
        this.compareMode = this.comparableSecurities.length > 0
    }

    updateScaleConfigurations() {
        const leftScaleGroup = this.chartConfig.leftScaleGroup
        const newConfig = { ...this.chartConfig }
        newConfig.scaleOptions = {
            ...newConfig.scaleOptions,
            leftScaleVisible: leftScaleGroup !== '' ? true : false
        }
        const newSeriesGroups: Record<string, SeriesGroupConfig> = {}

        Object.entries(newConfig?.seriesGroups ?? {}).forEach(([sgId, sg]) => {
            newSeriesGroups[sgId] = {
                ...sg,
                scale: leftScaleGroup === sgId ? 'LEFT' : 'RIGHT'
            }
        })
        this.updateChartConfig({
            ...newConfig,
            seriesGroups: {
                ...newSeriesGroups
            }
        })
    }

    getLeftScaleWidth() {
        try {
            return this.chartApi.priceScale('left').width()
        } catch (e) {
            return 0
        }
    }

    getRightScaleWidth() {
        try {
            return this.chartApi.priceScale('right').width()
        } catch (e) {
            return 0
        }
    }

    addIndicator(indicator: Indicator, pushToHistory = false): Promise<void> {
        return this.applyAddIndicator(indicator, pushToHistory)
    }

    private async applyAddIndicator(indicator: Indicator, pushToHistory = false): Promise<void> {
        const config = { ...this.chartConfig }
        const newSeriesGroupConfig: Record<string, SeriesGroupConfig> = { ...config.seriesGroups }
        let panes = [...config.panes]
        const newSeriesConfig: Record<string, Series> = { ...config.series }
        const newPanesConfig: Record<string, PaneConfig> = { ...config.panesConfig }

        const addPane = indicator.type !== 'OVERLAY'
        const category: SeriesGroupCategory = ['DRAWING'].includes(indicator.candleType)
            ? SeriesGroupCategory.SERIES_DRAWING
            : ['DRAWING_TOOL'].includes(indicator.candleType)
                ? SeriesGroupCategory.SERIES_DRAWING_TOOL
                : indicator.code === VOLUME_INDICATOR_CODE
                    ? SeriesGroupCategory.SERIES_VOLUME
                    : indicator.code === PIVOT_POINTS_CODE || isPatternIndicator(indicator)
                        ? SeriesGroupCategory.SERIES_DRAWING
                        : SeriesGroupCategory.SERIES_INDICATOR
        if (category === SeriesGroupCategory.SERIES_DRAWING_TOOL) {
            const drawingType = Object.keys(getDrawingClassMap()).find((drawingType) => indicator.code?.toLowerCase() === drawingType?.toLowerCase())
            if (drawingType) {
                this.drawingManager.addDrawing(drawingType as DrawingTypes, DrawingDisplayType.DRAWING, {}, {})
            }
            return
        }
        const seriesGrp: SeriesGroupConfig = {
            id: uuid(),
            name: indicator.name,
            scriptCode: indicator.code,
            securityList: [],
            addedTime: Date.now(),
            description: indicator.description,
            series: [],
            scale: this.getIndicatorSeriesScale(indicator, addPane),
            primary: false,
            compareMode: this.compareMode,
            category,
            inputValues: (indicator.inputs || []).reduce(
                (acc, input) => ({
                    ...acc,
                    [input.code]: input.defval,
                }),
                {} as Record<string, string>,
            ),
            outputValues: {}
        }

        indicator.outputs.forEach((output) => {
            const { title, type, colors, thickness, drawingType, drawingSettings, subType, visibility, style, code } = output
            const isArea = type === IndicatorOutput_OutputType.AREA
            const sr: Series = {
                id: uuid(),
                chartType: getChartTypeFromOutput(type),
                chartSubType: subType ?? ChartSubType.SIMPLE,
                title,
                code,
                name: title,
                drawing: type === IndicatorOutput_OutputType.DRAWING,
                drawingType: drawingType,
                drawingSettings: drawingSettings,
                color: colors?.length ? colors[0].colorCode : undefined,
                thickness: getLineWidth(thickness ?? 1),
                visibility: visibility ?? true,
                style,
                priceFormat: getIndicatorPriceType(indicator_FormatFromJSON(indicator.format)),
                ...(isArea ? { options: { ...getAreaSeriesColorOptions(colors) } as any } : {})
            }
            newSeriesConfig[sr.id] = sr
            seriesGrp.series.push(sr.id)
            seriesGrp.outputValues[sr.id] = output
        })

        const historyData = [
            {
                chartId: this.id,
                seriesGroupId: seriesGrp.id,
                indicator: indicator,
                paneId: ''
            }
        ]

        if (addPane) {
            const newPaneData: PaneConfig = {
                id: uuid(),
                seriesGroups: [seriesGrp.id],
                primary: false
            }
            panes.push(newPaneData.id)
            newPanesConfig[newPaneData.id] = newPaneData
            historyData[0].paneId = newPaneData.id
        } else {
            const [primaryPane] = Object.values(newPanesConfig).filter((p) => p.primary)
            const newPrimaryId = primaryPane.id
            if (primaryPane) {
                let volumeIndicatorId = ''
                primaryPane.seriesGroups.forEach((sgid) => {
                    const sg = newSeriesGroupConfig[sgid]
                    if (sg.scriptCode === VOLUME_INDICATOR_CODE) {
                        volumeIndicatorId = sgid
                    }
                })

                let updatedSeriesGroups = [...primaryPane.seriesGroups]
                if (volumeIndicatorId && indicator.code === VOLUME_INDICATOR_CODE) {
                    const oldSgRef = this.seriesGroupRef[volumeIndicatorId]
                    oldSgRef?.destroy()
                    delete this.seriesGroupRef[volumeIndicatorId]
                    const oldSgConfig = newSeriesGroupConfig[volumeIndicatorId]
                    oldSgConfig?.series.forEach((seriesId) => delete newSeriesConfig[seriesId])
                    delete newSeriesGroupConfig[volumeIndicatorId]
                    updatedSeriesGroups = updatedSeriesGroups.filter((sgid) => sgid !== volumeIndicatorId)
                }

                if (!updatedSeriesGroups.includes(seriesGrp.id)) {
                    updatedSeriesGroups.push(seriesGrp.id)
                }
                newPanesConfig[newPrimaryId] = {
                    ...primaryPane,
                    id: newPrimaryId,
                    seriesGroups: updatedSeriesGroups
                }
                panes = [newPrimaryId, ...panes.filter((v) => v !== primaryPane.id)]
            }
        }

        if (!pushToHistory) {
            this.historyManager.push(historyData, 'ADD INDICATOR', ActionType.ADD_INDICATOR)
        }
        newSeriesGroupConfig[seriesGrp.id] = seriesGrp

        const [primarySeriesGroup] = Object.values(newSeriesGroupConfig).filter((p) => p.primary)
        newSeriesGroupConfig[primarySeriesGroup.id] = {
            ...primarySeriesGroup
        }
        newSeriesGroupConfig[seriesGrp.id].securityList = primarySeriesGroup.securityList

        await this.updateChartConfig({
            ...config,
            panes,
            panesConfig: newPanesConfig,
            seriesGroups: newSeriesGroupConfig,
            series: newSeriesConfig
        })
        this.seriesGroupRef[seriesGrp.id]?.updateData(false, DataUpdateRequestType.INITIAL)
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    refreshAllSeriesGroups(recreate = true) {
        for (const key in this.seriesGroupRef) {
            const seriesGroup = this.seriesGroupRef[key]
            seriesGroup.refreshGroup(recreate)
        }
    }

    generateSecurityMap() {
        this.securityMap = this.securities.concat(this.comparableSecurities).reduce(
            (acc, sec) => {
                const key = getSecurityKey(sec?.exchange, sec?.code)
                return {
                    ...acc,
                    [key]: sec
                }
            },
            {} as Record<string, Security>
        )
    }

    getChartSnapshot(offset?: number): ChartSnapshot {
        const offsetIndex = offset !== undefined ? offset : this.currentCrosshairOffset || 0
        const crosshairTime = this.currentCrosshairTime
        let snapshot: ChartSnapshot = {
            primary: this.primarySeriesGroup?.getSeriesData(offsetIndex, crosshairTime) || {}
        }
        for (let sgid in this.chartConfig.seriesGroups) {
            const sg = this.seriesGroupRef[sgid]
            if (!sg) continue

            snapshot = {
                ...snapshot,
                [sgid]: {
                    ...sg.getSeriesData(offsetIndex, crosshairTime)
                }
            }
        }

        return snapshot
    }

    getPrimarySeriesMetadata(): Record<string, any> {
        const primarySeries = this.primarySeriesGroup?.getPrimarySeries()
        return primarySeries?.metadata ?? {}
    }

    toggleVisibility(sgid: string, visible: boolean) {
        const sg = this.seriesGroupRef[sgid]
        if (!sg) return false
        sg.toggleVisibility(visible)
        return true
    }

    removePane(index: number, publishUpdate: boolean = true, pushToHistory = false) {
        if (index === 0) {
            this.swapPanes(0, 1)
            index = 1
        }
        const paneId = this.chartConfig.panes[index]
        const pane = this.chartConfig.panesConfig[paneId]

        if (!pane) return

        const newConfig = {
            ...this.chartConfig,
            panes: [...this.chartConfig.panes],
            seriesGroups: { ...this.chartConfig.seriesGroups },
            series: { ...this.chartConfig.series },
            scaleOptions: { ...this.chartConfig.scaleOptions },
            comparableSecurityPanePlacement: { ...(this.chartConfig.comparableSecurityPanePlacement ?? {}) }
        }
        newConfig.panes = newConfig.panes.filter((v) => v !== paneId)

        const removedComparableSeriesGroupIds: string[] = []
        for (const sgid of pane.seriesGroups) {
            const sgConf = newConfig.seriesGroups[sgid]
            if (sgConf?.category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
                removedComparableSeriesGroupIds.push(sgid)
            }
        }

        if (removedComparableSeriesGroupIds.length) {
            const orderedBefore = getOrderedComparableSeriesGroupIds(
                this.chartConfig.panes,
                this.chartConfig.panesConfig,
                this.chartConfig.seriesGroups
            )
            const indicesToRemove = new Set(
                removedComparableSeriesGroupIds.map((sgid) => orderedBefore.indexOf(sgid)).filter((i) => i >= 0)
            )
            const sortedIdx = [...indicesToRemove].sort((a, b) => b - a)
            let comp = [...this.comparableSecurities]
            let pl = buildComparableSecurityPanePlacements(
                comp,
                newConfig.comparableSecurityPanePlacement,
                newConfig.comparableSecurityPanePlacements
            )
            for (const idx of sortedIdx) {
                if (idx < comp.length) {
                    comp.splice(idx, 1)
                    pl.splice(idx, 1)
                }
            }
            this.comparableSecurities = comp
            newConfig.comparableSecurities = comp
            newConfig.comparableSecurityPanePlacements = pl
            newConfig.comparableSecurityPanePlacement = legacyComparablePlacementRecord(comp, pl)
            this.updateCompareMode()
            this.generateSecurityMap()
            for (const sgid of removedComparableSeriesGroupIds) {
                if (newConfig.leftScaleGroup === sgid) {
                    newConfig.leftScaleGroup = ''
                }
            }
        }

        pane.seriesGroups.forEach((sgid) => {
            const sg = this.seriesGroupRef[sgid]
            sg.destroy(pushToHistory)
            delete newConfig.seriesGroups[sgid]
            delete this.seriesGroupRef[sgid]
            sg.series.forEach((srs) => delete newConfig.series[srs.id])
        })

        const primaryPaneForCompareReset = Object.values(newConfig.panesConfig).find((p) => p.primary)
        if (!this.compareMode && primaryPaneForCompareReset) {
            primaryPaneForCompareReset.seriesGroups.forEach((sgid) => {
                const sgRef = this.seriesGroupRef[sgid]
                let sg = newConfig.seriesGroups[sgid]
                if (!sg || !sgRef) return

                sg = {
                    ...sg,
                    compareMode: false
                }
                newConfig.seriesGroups[sgid] = sg
                sgRef.config = {
                    ...sgRef.config,
                    compareMode: false
                }
                if (!sgRef || sgRef.config.scriptCode === VOLUME_INDICATOR_CODE) return
            })
        }

        const primaryPaneEntry = Object.values(newConfig.panesConfig).find((p) => p.primary)
        const hasOverlayComparable = primaryPaneHasOverlayComparable(primaryPaneEntry, newConfig.seriesGroups)
        const activeScale = this.getActivePriceScaleSide()
        const layout = applyOverlayComparableLayout(
            newConfig.seriesGroups,
            newConfig.scaleOptions,
            activeScale,
            hasOverlayComparable,
            true
        )
        newConfig.seriesGroups = layout.seriesGroups
        if (!hasOverlayComparable) {
            newConfig.scaleOptions = {
                ...layout.scaleOptions,
                rightScaleMode: PriceScaleMode.Normal,
                leftScaleMode: PriceScaleMode.Normal,
                leftScaleVisible: activeScale === 'LEFT',
                rightScaleVisible: activeScale === 'RIGHT',
                percentageModeReferenceSourceIndex: 0,
            }
        } else {
            newConfig.scaleOptions = layout.scaleOptions
        }

        this.chartApi.removePane(index)
        this.updateScaleOptions(newConfig.scaleOptions, newConfig.seriesGroups)
        this.updateChartConfig(newConfig).then(() => {
            if (publishUpdate) this.callbacks.onConfigUpdate(this.chartConfig)
        })
    }

    removeAllIndicators() {
        for (let i = this.chartConfig.panes.length - 1; i >= 0; i--) {
            const pane = this.chartConfig.panes[i]
            const paneConfig = this.chartConfig.panesConfig[pane]
            if (!paneConfig.primary) {
                this.removePane(i, false)
            } else {
                paneConfig.seriesGroups.forEach((seriesGrp) => {
                    if (this.chartConfig.seriesGroups[seriesGrp].category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY) {
                        this.removeComparableSecurity(seriesGrp, false)
                        return
                    } else {
                        if (!this.chartConfig.seriesGroups[seriesGrp].primary) {
                            this.removeSeries(seriesGrp, false)
                        }
                    }
                })
            }
        }
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    removeSeries(seriesGroupId: string, publishUpdate = true, pushToHistory = false) {
        const newConfig = {
            ...this.chartConfig,
            panes: [...this.chartConfig.panes],
            panesConfig: { ...this.chartConfig.panesConfig },
            seriesGroups: { ...this.chartConfig.seriesGroups },
            series: { ...this.chartConfig.series }
        }
        const [paneId] = newConfig.panes.filter((pid) => newConfig.panesConfig[pid] && newConfig.panesConfig[pid].seriesGroups.indexOf(seriesGroupId) !== -1)
        if (!paneId || !newConfig.panesConfig[paneId]) return
        const pane = newConfig.panesConfig[paneId]
        const sg = this.seriesGroupRef[seriesGroupId]
        sg.destroy(pushToHistory)
        delete newConfig.seriesGroups[seriesGroupId]
        delete this.seriesGroupRef[seriesGroupId]
        sg.series.forEach((srs) => delete newConfig.series[srs.id])

        newConfig.panesConfig = {
            ...newConfig.panesConfig,
            [paneId]: {
                ...pane,
                seriesGroups: pane.seriesGroups.filter((sgid) => sgid !== seriesGroupId)
            }
        }
        this.updateChartConfig(newConfig)
        if (publishUpdate) this.callbacks.onConfigUpdate(this.chartConfig)
    }

    removeComparableSecurity(seriesGroupId: string, publishUpdate: boolean = true, pushToHistory: boolean = false) {
        const newConfig = {
            ...this.chartConfig,
            panes: [...this.chartConfig.panes],
            panesConfig: { ...this.chartConfig.panesConfig },
            seriesGroups: { ...this.chartConfig.seriesGroups },
            series: { ...this.chartConfig.series },
            scaleOptions: { ...this.chartConfig.scaleOptions },
            comparableSecurityPanePlacement: { ...(this.chartConfig.comparableSecurityPanePlacement ?? {}) },
            comparableSecurityPanePlacements: [...(this.chartConfig.comparableSecurityPanePlacements ?? [])]
        }
        const [paneId] = newConfig.panes.filter((pid) => newConfig.panesConfig[pid] && newConfig.panesConfig[pid].seriesGroups.indexOf(seriesGroupId) !== -1)
        if (!paneId || !newConfig.panesConfig[paneId]) return
        const seriesGroup = newConfig.seriesGroups[seriesGroupId]
        const pane = newConfig.panesConfig[paneId]
        const sg = this.seriesGroupRef[seriesGroupId]
        const historyData: Array<{
            chartId: string
            seriesGroupId: string
            security: any
            paneId: string
        }> = []
        this.comparableSecurities.forEach((security) => {
            historyData.push({
                chartId: this.id,
                seriesGroupId: seriesGroupId,
                security: security,
                paneId: paneId
            })
        })
        const orderedBefore = getOrderedComparableSeriesGroupIds(
            this.chartConfig.panes,
            this.chartConfig.panesConfig,
            this.chartConfig.seriesGroups
        )
        const removeIdx = orderedBefore.indexOf(seriesGroupId)
        sg.destroy()
        delete newConfig.seriesGroups[seriesGroupId]
        delete this.seriesGroupRef[seriesGroupId]
        sg.series.forEach((srs) => delete newConfig.series[srs.id])
        newConfig.panesConfig = {
            ...newConfig.panesConfig,
            [paneId]: {
                ...pane,
                seriesGroups: pane.seriesGroups.filter((sgid) => sgid !== seriesGroupId)
            }
        }
        const comp = [...this.comparableSecurities]
        const pl = buildComparableSecurityPanePlacements(
            comp,
            newConfig.comparableSecurityPanePlacement,
            newConfig.comparableSecurityPanePlacements
        )
        if (removeIdx >= 0 && removeIdx < comp.length) {
            comp.splice(removeIdx, 1)
            pl.splice(removeIdx, 1)
        }
        this.comparableSecurities = comp
        newConfig.comparableSecurities = comp
        newConfig.comparableSecurityPanePlacements = pl
        newConfig.comparableSecurityPanePlacement = legacyComparablePlacementRecord(comp, pl)
        if (seriesGroupId === newConfig.leftScaleGroup) {
            newConfig.leftScaleGroup = ''
        }

        this.updateCompareMode()
        const primaryPaneForCompareReset = Object.values(newConfig.panesConfig).find((p) => p.primary)
        if (!this.compareMode && primaryPaneForCompareReset) {
            primaryPaneForCompareReset.seriesGroups.forEach((sgid) => {
                const sgRef = this.seriesGroupRef[sgid]
                let sg = newConfig.seriesGroups[sgid]
                if (!sg || !sgRef) return

                sg = {
                    ...sg,
                    compareMode: false
                }
                newConfig.seriesGroups[sgid] = sg
                sgRef.config = {
                    ...sgRef.config,
                    compareMode: false
                }
                if (!sgRef || sgRef.config.scriptCode === VOLUME_INDICATOR_CODE) return
                // sg.refreshCompareMode()
            })
        }

        const primaryPaneEntry = Object.values(newConfig.panesConfig).find((p) => p.primary)
        const hasOverlayComparable = primaryPaneHasOverlayComparable(primaryPaneEntry, newConfig.seriesGroups)
        const activeScale = this.getActivePriceScaleSide()
        const layout = applyOverlayComparableLayout(
            newConfig.seriesGroups,
            newConfig.scaleOptions,
            activeScale,
            hasOverlayComparable,
            true
        )
        newConfig.seriesGroups = layout.seriesGroups
        if (!hasOverlayComparable) {
            newConfig.scaleOptions = {
                ...layout.scaleOptions,
                rightScaleMode: PriceScaleMode.Normal,
                leftScaleMode: PriceScaleMode.Normal,
                leftScaleVisible: activeScale === 'LEFT',
                rightScaleVisible: activeScale === 'RIGHT',
                percentageModeReferenceSourceIndex: 0,
            }
        } else {
            newConfig.scaleOptions = layout.scaleOptions
        }

        const updatedPane = newConfig.panesConfig[paneId]
        if (updatedPane && updatedPane.seriesGroups.length === 0 && !updatedPane.primary) {
            const paneIndex = newConfig.panes.indexOf(paneId)
            if (paneIndex >= 0 && this.chartApi) {
                this.chartApi.removePane(paneIndex)
            }
            const { [paneId]: _p, ...restPanesConfig } = newConfig.panesConfig
            newConfig.panesConfig = restPanesConfig
            newConfig.panes = newConfig.panes.filter((pid) => pid !== paneId)
        }
        this.chartConfig = { ...newConfig }
        this.updateScaleOptions(newConfig.scaleOptions, newConfig.seriesGroups)
        // this.checkScaleConfigurations() // TODO: check
        this.updateChartConfig(this.chartConfig)
        if (publishUpdate) this.callbacks.onConfigUpdate(this.chartConfig)
        if (!pushToHistory) {
            this.historyManager.push(historyData, 'Remove Comparable Security', ActionType.REMOVE_COMPARE_SECURITY_INDICATOR)
        }
    }

    getDrawingIds(seriesGroupId: string): string[] {
        const sg = this.seriesGroupRef[seriesGroupId]
        return sg?.getDrawingIds() ?? []
    }

    getSeriesIdOfDrawingId(seriesGroupId: string, drawingId: string): string | undefined {
        const sg = this.seriesGroupRef[seriesGroupId]
        return sg?.getSeriesIdOfDrawingId(drawingId) ?? undefined
    }

    swapPanes(from: number, to: number) {
        this.chartApi.swapPane(from, to)
        const newConfig = {
            ...this.chartConfig,
            panes: [...this.chartConfig.panes]
        }

        const temp = newConfig.panes[from]
        newConfig.panes[from] = newConfig.panes[to]
        newConfig.panes[to] = temp

        this.updateChartConfig(newConfig)
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    getIndicatorActivePlotTitles(seriesGroupId: string): ReadonlySet<string> | undefined {
        const sg = this.seriesGroupRef[seriesGroupId]
        if (sg instanceof IndicatorSeriesGroup) {
            return new Set(sg.activePlotTitles)
        }
        return undefined
    }

    getIndicatorOutputSourceLabel(sourceValue: string): string {
        if (isStableIndicatorOutputSource(sourceValue)) {
            const stableRef = decodeStableIndicatorOutputSource(sourceValue)
            if (stableRef) {
                const producerId = findSeriesGroupIdForStableIndicatorOutputSource(
                    sourceValue,
                    this.chartConfig.seriesGroups,
                    this.indicators
                )
                const producer = producerId ? this.seriesGroupRef[producerId] : undefined
                const indicator =
                    producer instanceof IndicatorSeriesGroup
                        ? producer.indicator
                        : this.indicators.find((item) => item.code === stableRef.indicatorCode)
                if (indicator) {
                    const output = indicator.outputs.find((item) => item.code === stableRef.outputCode)
                    const shortName = indicator.shortName || indicator.code
                    return formatIndicatorOutputSourceLabel(shortName, output?.title ?? stableRef.outputCode)
                }
            }
        }

        const ref = decodeIndicatorOutputSource(sourceValue)
        if (!ref) {
            return getIndicatorPriceSourceLabel(sourceValue)
        }

        const producer = this.seriesGroupRef[ref.seriesGroupId]
        if (!(producer instanceof IndicatorSeriesGroup)) {
            return sourceValue
        }

        const output = producer.indicator.outputs.find((item) => item.code === ref.outputCode)
        const shortName = producer.indicator.shortName || producer.indicator.code
        return formatIndicatorOutputSourceLabel(shortName, output?.title ?? ref.outputCode)
    }

    getAvailableIndicatorOutputSources(consumerSeriesGroupId: string): IndicatorOutputSourceOption[] {
        const plottableOutputTypes = new Set([
            IndicatorOutput_OutputType.LINE,
            IndicatorOutput_OutputType.HISTOGRAM,
            IndicatorOutput_OutputType.AREA,
            IndicatorOutput_OutputType.COLUMN,
            IndicatorOutput_OutputType.HZ_LINE,
        ])

        const options: IndicatorOutputSourceOption[] = []

        Object.entries(this.seriesGroupRef).forEach(([seriesGroupId, seriesGroup]) => {
            if (seriesGroupId === consumerSeriesGroupId) return
            if (!(seriesGroup instanceof IndicatorSeriesGroup)) return

            if (wouldCreateIndicatorSourceCycle(
                consumerSeriesGroupId,
                seriesGroupId,
                this.chartConfig.seriesGroups,
                this.indicators
            )) {
                return
            }

            const { indicator } = seriesGroup
            indicator.outputs.forEach((output) => {
                if (!plottableOutputTypes.has(output.type)) return
                if (!output.code || !output.title) return
                if (!seriesGroup.activePlotTitles.has(output.title)) return

                options.push({
                    value: encodeIndicatorOutputSource({
                        seriesGroupId,
                        outputCode: output.code,
                    }),
                    label: formatIndicatorOutputSourceLabel(
                        indicator.shortName || indicator.code,
                        output.title
                    ),
                    seriesGroupId,
                    outputCode: output.code,
                })
            })
        })

        return options
    }

    getIndicatorSourceDependents(seriesGroupId: string): string[] {
        return getIndicatorSourceDependents(seriesGroupId, this.chartConfig.seriesGroups, this.indicators)
    }

    getIndicatorSourceDependentDisplays(seriesGroupId: string) {
        return getIndicatorSourceDependentDisplays(seriesGroupId, this.chartConfig.seriesGroups, this.indicators)
    }

    getIndicatorSourceDependentDisplayNames(seriesGroupId: string): string[] {
        return getIndicatorSourceDependentDisplayNames(seriesGroupId, this.chartConfig.seriesGroups, this.indicators)
    }

    removeSeriesGroupWithPaneHandling(seriesGroupId: string, publishUpdate = true, pushToHistory = false) {
        const [paneId] = this.chartConfig.panes.filter(
            (pid) => this.chartConfig.panesConfig[pid]?.seriesGroups.indexOf(seriesGroupId) !== -1
        )
        if (!paneId || !this.chartConfig.panesConfig[paneId]) return

        const pane = this.chartConfig.panesConfig[paneId]
        if (pane.seriesGroups.length === 1) {
            const paneIndex = this.chartConfig.panes.indexOf(paneId)
            this.removePane(paneIndex, publishUpdate, pushToHistory)
        } else {
            this.removeSeries(seriesGroupId, publishUpdate, pushToHistory)
        }
    }

    removeIndicatorSeriesGroupTree(seriesGroupId: string, publishUpdate = true, pushToHistory = false) {
        const dependents = this.getIndicatorSourceDependents(seriesGroupId)
        const toRemove = [...dependents, seriesGroupId]
        const ordered = sortIndicatorSeriesGroupsForEvaluation(
            toRemove,
            this.chartConfig.seriesGroups,
            this.indicators
        ).reverse()

        ordered.forEach((sgid, index) => {
            const isLast = index === ordered.length - 1
            this.removeSeriesGroupWithPaneHandling(
                sgid,
                publishUpdate && isLast,
                pushToHistory && sgid === seriesGroupId
            )
        })
    }

    private getOrderedIndicatorSeriesGroupIds(): string[] {
        const indicatorSeriesGroupIds = Object.keys(this.seriesGroupRef).filter((seriesGroupId) => {
            const seriesGroup = this.seriesGroupRef[seriesGroupId]
            return !seriesGroup.primary && seriesGroup instanceof IndicatorSeriesGroup
        })

        return sortIndicatorSeriesGroupsForEvaluation(
            indicatorSeriesGroupIds,
            this.chartConfig.seriesGroups,
            this.indicators
        )
    }

    private async updateIndicatorSeriesGroups(newDataFetched: boolean, updateType: DataUpdateRequestType) {
        const orderedIds = this.getOrderedIndicatorSeriesGroupIds()
        for (const seriesGroupId of orderedIds) {
            if (this.destroyed || !this.chartApi) return
            const seriesGroup = this.seriesGroupRef[seriesGroupId]
            if (!seriesGroup || seriesGroup.primary) continue
            await seriesGroup.updateData(newDataFetched, updateType)
        }
    }

    private async updateNonIndicatorSeriesGroups(newDataFetched: boolean, updateType: DataUpdateRequestType) {
        for (const seriesGroupId in this.seriesGroupRef) {
            const seriesGroup = this.seriesGroupRef[seriesGroupId]
            if (!seriesGroup || seriesGroup.primary || seriesGroup instanceof IndicatorSeriesGroup) continue
            await seriesGroup.updateData(newDataFetched, updateType)
        }
    }

    updateSeriesGroupInputs(seriesGroupId: string, inputValues: SeriesGroupConfig['inputValues'], refresh: boolean) {
        const sg = this.seriesGroupRef[seriesGroupId]

        if (!sg) return

        const newConfig = {
            ...this.chartConfig
        }

        newConfig.seriesGroups = {
            ...newConfig.seriesGroups,
            [seriesGroupId]: {
                ...newConfig.seriesGroups[seriesGroupId],
                inputValues
            }
        }

        this.updateChartConfig(newConfig).then(() => {
            if (refresh) {
                sg.refreshGroup()
            }
        })
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    updateSeriesGroupOutputValuesPrecision(seriesGroupId: string, outputValuesPrecision: number | undefined) {
        const sg = this.seriesGroupRef[seriesGroupId]
        if (!sg) return

        const newConfig = { ...this.chartConfig }
        const groupConfig = { ...newConfig.seriesGroups[seriesGroupId] }
        if (outputValuesPrecision === undefined) {
            delete groupConfig.outputValuesPrecision
        } else {
            groupConfig.outputValuesPrecision = outputValuesPrecision
        }
        newConfig.seriesGroups = {
            ...newConfig.seriesGroups,
            [seriesGroupId]: groupConfig,
        }

        this.chartConfig = newConfig
        sg.config = groupConfig
        sg.refreshGroup(false)
        this.callbacks.onConfigUpdate(this.chartConfig)
        notifyChartLiveDataForSeriesGroup(this.id, seriesGroupId)
    }

    updateSeriesGroupDisplaySettings(
        seriesGroupId: string,
        displaySettings: Partial<Pick<SeriesGroupConfig, 'showLabelOnPriceScale' | 'showValueOnPriceScale'>>
    ) {
        const sg = this.seriesGroupRef[seriesGroupId]
        if (!sg) return

        const newConfig = { ...this.chartConfig }
        const groupConfig = { ...newConfig.seriesGroups[seriesGroupId] }
        let priceScaleDisplayChanged = false

        if ('showLabelOnPriceScale' in displaySettings) {
            const value = displaySettings.showLabelOnPriceScale
            if (value === undefined || value === true) {
                if (groupConfig.showLabelOnPriceScale !== undefined) {
                    delete groupConfig.showLabelOnPriceScale
                    priceScaleDisplayChanged = true
                }
            } else if (groupConfig.showLabelOnPriceScale !== value) {
                groupConfig.showLabelOnPriceScale = value
                priceScaleDisplayChanged = true
            }
        }

        if ('showValueOnPriceScale' in displaySettings) {
            const value = displaySettings.showValueOnPriceScale
            if (value === undefined || value === true) {
                if (groupConfig.showValueOnPriceScale !== undefined) {
                    delete groupConfig.showValueOnPriceScale
                    priceScaleDisplayChanged = true
                }
            } else if (groupConfig.showValueOnPriceScale !== value) {
                groupConfig.showValueOnPriceScale = value
                priceScaleDisplayChanged = true
            }
        }

        newConfig.seriesGroups = {
            ...newConfig.seriesGroups,
            [seriesGroupId]: groupConfig,
        }

        this.chartConfig = newConfig
        sg.config = groupConfig
        if (priceScaleDisplayChanged) {
            sg.refreshGroup(false)
        }
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    setSeriesGroupError(seriesGroupId: string, value: string) {
        const sg = this.seriesGroupRef[seriesGroupId]

        if (!sg) return

        if (value) {
            this.seriesGroupErrors[seriesGroupId] = value
            sg.config.error = value
        } else {
            delete this.seriesGroupErrors[seriesGroupId]
            sg.config.error = undefined
        }
        this.callbacks.onSeriesGroupError?.(seriesGroupId, value || undefined)
    }

    updateCompareSeriesLineStyle(seriesGroupId: string, seriesId: string, lineStyle: ChartLineStyle) {
        const sg = this.seriesGroupRef[seriesGroupId]
        const chartSeries = sg?.chartSeries[seriesId]
        if (!chartSeries) return
        chartSeries.updateOptions({ lineStyle: canvasLineStyleToLineStyle(lineStyle) } as any)
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    updateSeriesGroupOutputs(seriesGroupId: string, outputValues: SeriesGroupConfig['outputValues']) {
        const sg = this.seriesGroupRef[seriesGroupId]

        if (!sg) return

        const newConfig = {
            ...this.chartConfig
        }

        newConfig.seriesGroups = {
            ...newConfig.seriesGroups,
            [seriesGroupId]: {
                ...newConfig.seriesGroups[seriesGroupId],
                outputValues
            }
        }

        newConfig.series = { ...newConfig.series }
        for (const sid in outputValues) {
            const { style, type, colors, thickness, drawingType, drawingSettings, subType, visibility } = outputValues[sid]
            const isArea = type === IndicatorOutput_OutputType.AREA
            newConfig.series[sid] = {
                ...newConfig.series[sid],
                chartType: type ? getChartTypeFromOutput(type) : newConfig.series[sid].chartType,
                chartSubType: subType ?? ChartSubType.SIMPLE,
                color: colors?.length ? colors[0].colorCode : undefined,
                thickness: getLineWidth(thickness ?? 1),
                drawingType: drawingType,
                style,
                drawingSettings: drawingSettings,
                drawing: type === IndicatorOutput_OutputType.DRAWING,
                visibility: visibility ?? true,
                ...(isArea ? { options: { ...newConfig.series[sid]?.options, ...getAreaSeriesColorOptions(colors) } as any } : {})
            }
        }

        this.updateChartConfig(newConfig).then(() => {
            sg.refreshGroup(false)
            for (const sid in outputValues) {
                const output = outputValues[sid]
                if (output?.drawingType && output.type === IndicatorOutput_OutputType.DRAWING) {
                    sg.updateOutputDrawingSettings(sid)
                }
            }
            sg.updateData(false, DataUpdateRequestType.INITIAL)
        })
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    updateSeriesGroupOutputsAndInputs(seriesGroupId: string, outputValues: SeriesGroupConfig['outputValues'], inputValues: SeriesGroupConfig['inputValues'], refresh: boolean) {
        const sg = this.seriesGroupRef[seriesGroupId]

        if (!sg) return

        const newConfig = {
            ...this.chartConfig
        }

        newConfig.seriesGroups = {
            ...newConfig.seriesGroups,
            [seriesGroupId]: {
                ...newConfig.seriesGroups[seriesGroupId],
                inputValues,
                outputValues
            }
        }

        newConfig.series = { ...newConfig.series }
        for (const sid in outputValues) {
            const { type, colors, thickness, drawingType, drawingSettings, subType } = outputValues[sid]
            const isArea = type === IndicatorOutput_OutputType.AREA
            newConfig.series[sid] = {
                ...newConfig.series[sid],
                chartType: type ? getChartTypeFromOutput(type) : newConfig.series[sid].chartType,
                chartSubType: subType ?? ChartSubType.SIMPLE,
                color: colors?.length ? colors[0].colorCode : undefined,
                thickness: getLineWidth(thickness ?? 1),
                drawingType: drawingType,
                drawingSettings: drawingSettings,
                drawing: type === IndicatorOutput_OutputType.DRAWING,
                ...(isArea ? { options: { ...newConfig.series[sid]?.options, ...getAreaSeriesColorOptions(colors) } as any } : {})
            }
        }

        this.updateChartConfig(newConfig)
        for (const sid in outputValues) {
            const output = outputValues[sid]
            if (output?.drawingType && output.type === IndicatorOutput_OutputType.DRAWING) {
                sg.updateOutputDrawingSettings(sid)
            }
        }
        if (refresh) {
            sg.refreshGroup()
        }
        this.callbacks.onConfigUpdate(this.chartConfig)
    }

    setSelectedDateRange(dateRange: SuperChartConfiguration_DateRange, temporary: boolean = true, silent = false) {
        const [timeBucket] = this.timeBuckets.filter((v) => v.code === dateRange.timeBucketCode)
        if (!timeBucket) return
        this.timeBucket = timeBucket
        this.dateRange = dateRange
        this.isDateRangeTemporary = temporary

        !silent &&
            this.updateChartConfig({
                ...this.chartConfig,
                timeBucket
            }).then(() => {
                this.fetchData({ initialize: true, updateConfig: true })
            })
    }

    setVisibleLogicalRange(from: number, to: number, reassignHandler: boolean = false) {
        if (reassignHandler) {
            this.primarySeriesGroup?.unsubscribeVisibleLogicalRangeChange()
            this.unsubscribeVisibleLogicalRange()
        }
        const { from: fromAdj, to: toAdj } = this.applyOverlayTimeScaleLogicalMargin(from, to)
        this.chartApi.timeScale().setVisibleLogicalRange({
            from: fromAdj,
            to: toAdj
        })
        if (reassignHandler) {
            this.primarySeriesGroup?.subscribeVisibleLogicalRangeChange()
            this.subscribeVisibleLogicalRange()
        }
    }

    // setVisibleRange()

    setViewportCandleInfo(info: ViewportCandleInfo) {
        this.viewportCandleInfo = info
    }

    resetViewportCandleInfo() {
        this.viewportCandleInfo = {
            barsBefore: 0,
            barsAfter: 0,
            rangeStart: 0,
            rangeEnd: 0,
            startTime: 0,
            endTime: 0,
            viewportCandles: 0
        }
    }

    isViewportInfoSet() {
        const { barsBefore, barsAfter, endTime, rangeEnd, rangeStart, startTime, viewportCandles, middlePointTime } = this.viewportCandleInfo
        return barsBefore || barsAfter || endTime || rangeEnd || rangeStart || startTime || viewportCandles || middlePointTime
    }

    getRenderedPrimaryData() {
        if (!this.primarySeriesGroup) return []

        const seriesConfig = this.primarySeriesGroup.series[0]
        const series = this.primarySeriesGroup.chartSeries[seriesConfig?.id]
        if (!series) return []
        return series.getData()
    }

    getRenderedBlankData() {
        if (!this.primarySeriesGroup) return []

        const seriesConfig = this.primarySeriesGroup.series[1]
        const series = this.primarySeriesGroup.chartSeries[seriesConfig?.id].seriesApi
        if (!series) return []
        return series.data() as ChartCandle[]
    }

    goToDate(timeInSeconds: number, notifyChart: boolean = false) {
        const zonedTime = convertToTimezone(timeInSeconds, this.timezone)
        const data = this.getRenderedPrimaryData()
        const start = data[0],
            end = data[data.length - 1]
        this.gotoDate = zonedTime
        this.notifyGoToDateToChart = notifyChart

        this.previousViewPortCandleInfo = {
            ...this.viewportCandleInfo,
            startTime: 0,
            endTime: 0
        }
        if ((start && (start.time as number)) > zonedTime || (end && (end.time as number)) < zonedTime) {
            this.fetchData({ initialize: true })
        } else {
            this.focusTimeInViewPort(zonedTime, data)
        }
    }

    // all times are in utc,
    focusTimeRange(viewportCandleInfo: ViewportCandleInfo, interval: SuperChartConfiguration_TimeBucket, gotoDate: number | undefined) {
        if (this._mouseEntered) return // to avoid circular updates
        const { startTime, endTime, rangeStart, rangeEnd, barsAfter, barsBefore } = viewportCandleInfo
        const timeWidth = getTimeWidthInSeconds(interval)
        const currentTimeWidth = getTimeWidthInSeconds(this.chartConfig.timeBucket)
        const multiplier = timeWidth / currentTimeWidth
        const data = this.getRenderedPrimaryData()
        let startIndex = getTimeIndex(data, startTime)
        let endIndex = getTimeIndex(data, endTime)
        if (Number.isFinite(startIndex) && barsBefore < 0) startIndex += barsBefore * multiplier
        if (Number.isFinite(startIndex) && Number.isFinite(endIndex)) {
            this.setVisibleLogicalRange(startIndex, endIndex)
        } else {
            this.fetchData({ initialize: false, fetchCompulsory: true }).then((newDataFetched) => {
                newDataFetched && this.focusTimeRange(viewportCandleInfo, interval, gotoDate)
            })
            return
        }

        if (gotoDate !== undefined) {
            this.goToDate(gotoDate)
        }
    }

    setVisibleRange(startTime: Time, endTime: Time) {
        this.chartApi.timeScale().setVisibleRange({
            from: startTime as Time,
            to: endTime as Time
        })
    }

    gotoCurrentTime() {
        const data = this.getRenderedPrimaryData()
        if (!data.length) return
        const latestCandle = data[data.length - 1]

        if (toDayFormat((latestCandle.time as number) * 1000) === toDayFormat(Date.now())) {
            const { viewportCandles } = this.viewportCandleInfo
            this.setVisibleLogicalRange(data.length - viewportCandles - (this.noOffset ? 0 : RIGHT_CANDLES_OFFSET), data.length + (this.noOffset ? 0 : RIGHT_CANDLES_OFFSET))
        } else {
            this.fetchData({ initialize: true })
        }
    }

    /** Zoom in by reducing visible bars (TradingView-like behavior) */
    zoomIn() {
        if (!this.chartApi) return
        const range = this.chartApi.timeScale().getVisibleLogicalRange()
        if (!range) return

        const center = (range.from + range.to) / 2
        const currentSpan = range.to - range.from
        const newSpan = currentSpan * 0.8 // Zoom in by 20%

        this.setVisibleLogicalRange(
            Math.max(0, center - newSpan / 2),
            center + newSpan / 2
        )
    }

    /** Zoom out by increasing visible bars (TradingView-like behavior) */
    zoomOut() {
        if (!this.chartApi) return
        const range = this.chartApi.timeScale().getVisibleLogicalRange()
        if (!range) return

        const center = (range.from + range.to) / 2
        const currentSpan = range.to - range.from
        const newSpan = currentSpan * 1.25 // Zoom out by 25%

        this.setVisibleLogicalRange(
            Math.max(0, center - newSpan / 2),
            center + newSpan / 2
        )
    }

    /** Pan chart left by moving viewport (TradingView-like behavior) */
    panLeft(fast = false) {
        if (!this.chartApi) return
        const range = this.chartApi.timeScale().getVisibleLogicalRange()
        if (!range) return

        const currentSpan = range.to - range.from
        const panAmount = fast ? currentSpan * 0.5 : currentSpan * 0.1 // 50% for fast pan, 10% for normal

        this.setVisibleLogicalRange(
            Math.max(0, range.from - panAmount),
            range.to - panAmount
        )
    }

    /** Pan chart right by moving viewport (TradingView-like behavior) */
    panRight(fast = false) {
        if (!this.chartApi) return
        const range = this.chartApi.timeScale().getVisibleLogicalRange()
        if (!range) return

        const currentSpan = range.to - range.from
        const panAmount = fast ? currentSpan * 0.5 : currentSpan * 0.1 // 50% for fast pan, 10% for normal

        this.setVisibleLogicalRange(
            range.from + panAmount,
            range.to + panAmount
        )
    }

    /** Move chart to the first bar */
    moveToFirstBar() {
        const data = this.getRenderedPrimaryData()
        if (!data.length) return
        const { viewportCandles } = this.viewportCandleInfo
        this.setVisibleLogicalRange(0, viewportCandles)
    }

    /** Move chart to the last/most recent bar */
    moveToLastBar() {
        this.gotoCurrentTime()
    }

    /** Move chart to realtime (same as last bar for now) */
    moveToRealtimeBar() {
        this.gotoCurrentTime()
    }

    focusTimeInViewPort(zonedTimeInSeconds: number, data: SeriesData[], resetWhitespace = false) {
        if (!data || !data.length) return
        const index = getTimeIndex(data as ChartCandle[], zonedTimeInSeconds)
        let from = NaN,
            to = NaN
        const viewportCandles = this.previousViewPortCandleInfo.viewportCandles

        if (this.persistViewport && this.previousViewPortCandleInfo.startTime && this.previousViewPortCandleInfo.endTime) {
            from = getTimeIndex(data as ChartCandle[], this.previousViewPortCandleInfo.startTime) || 0
            to = getTimeIndex(data as ChartCandle[], this.previousViewPortCandleInfo.endTime) || data.length
            if (resetWhitespace && to > data.length + RIGHT_CANDLES_OFFSET) {
                to = data.length + RIGHT_CANDLES_OFFSET
            }
        } else if (Number.isFinite(index)) {
            from = index - Math.floor(viewportCandles / 2)
            to = index + Math.ceil(viewportCandles / 2)
        }
        if (Number.isFinite(from) && Number.isFinite(to)) {
            this.setVisibleLogicalRange(from, to, true)
        }
        setTimeout(() => {
            this.notifyGoToDateToChart && this.callbacks.onGotoDateUpdated(data[index].time as number)
            this.notifyGoToDateToChart = false
            this.gotoDate = undefined
        }, 500) // giving 600ms time so that gotodate is updated only after the visible logical range is updated completely
    }

    setCrossHairPosition(price: number, time: number) {
        const primarySeries = this.primarySeriesGroup?.getPrimarySeriesApi()
        if (!primarySeries) return
        const data = this.getRenderedPrimaryData()
        if (!data || !data.length) return
        const index = getTimeIndex(data as ChartCandle[], time)
        if (Number.isFinite(index) && data[index]) {
            const x = this.chartApi.timeScale().timeToCoordinate(data[index].time)
            const y = primarySeries.priceToCoordinate(price)
            if (!x || !y) return
            this.chartApi.setCrosshairPosition(price, data[index].time, primarySeries)
            this.onCrosshairMove(
                {
                    seriesData: new Map(),
                    point: { x, y },
                    time: data[index].time
                },
                false
            )
        } else {
            this.chartApi.clearCrosshairPosition()
        }
    }

    onMouseDown = () => {
        this.callbacks.onMouseDown && this.callbacks.onMouseDown()
    }

    onMouseEnter = () => {
        this.containerBounds = this.container.getBoundingClientRect()
        this._mouseEntered = true
        this.callbacks.onMouseEnter && this.callbacks.onMouseEnter()
    }

    /** Non-embedded + touch: set ChartContext hover on first intentional touch (avoid synthetic mousemove path). */
    private onTouchChartHoverStart = (e: TouchEvent) => {
        if (this.destroyed || !this.touchEnabled) return
        if (!this.containerBounds) {
            this.containerBounds = this.container.getBoundingClientRect()
        }
        const t = e.touches[0]
        if (!t) return
        const { left, right, top, bottom } = this.containerBounds
        const withinBounds =
            t.clientX > left && t.clientX < right && t.clientY > top && t.clientY < bottom
        if (!withinBounds) return
        this.hoverActive = true
        if (!this._mouseEntered) {
            this._mouseEntered = true
            this.callbacks.onMouseEnter && this.callbacks.onMouseEnter()
        }
    }

    onMouseMove = (event: any) => {
        if (!this.containerBounds) {
            this.containerBounds = this.container.getBoundingClientRect()
        }
        const { left, right, top, bottom } = this.containerBounds
        const x = event.clientX,
            y = event.clientY
        const withinBounds = x > left && x < right && y > top && y < bottom

        this.hoverActive = withinBounds
        if (!withinBounds) {
            this.onMouseLeave()
        } else {
            if (!this._mouseEntered) {
                this._mouseEntered = true
                this.callbacks.onMouseEnter && this.callbacks.onMouseEnter()
            }
            this.callbacks.onMouseMove && this.callbacks.onMouseMove()
        }
    }

    onMouseLeave = () => {
        setChartHoveredPane(this.id, undefined)
        this.hoveredPaneId = undefined
        this.crosshairHoveredSeries = undefined
        this.setSeriesHoverCursor(false)
        this.callbacks.onCrosshairMove(this.crossHairInfo)
        this.callbacks.onMouseLeave && this.callbacks.onMouseLeave()
        this._mouseEntered = false
    }

    removeDateChangeListener = () => {
        this.callbacks.onTimeRangeChange = undefined
    }

    updateTimeRangeChangeListener = (listener: (viewportCandleInfo: ViewportCandleInfo) => void) => {
        this.callbacks.onTimeRangeChange = listener
    }

    updateCallbackListeners(callbacks: Partial<ChartCallbacks>) {
        this.callbacks = {
            ...this.callbacks,
            ...callbacks
        }
    }

    removeCallbackListeners(callbacks: Partial<ChartCallbacks>) {
        for (let key in callbacks) {
            if (callbacks[key as keyof ChartCallbacks] === this.callbacks[key as keyof ChartCallbacks]) {
                delete callbacks[key as keyof ChartCallbacks]
            }
        }
    }

    subscribeEvent(event: ChartEvents, callback: (event: CustomEvent) => void) {
        this.eventBus.subscribe(event, callback)
    }

    unsubscribeEvent(event: ChartEvents, callback: (event: CustomEvent) => void) {
        this.eventBus.unsubscribe(event, callback)
    }

    publishEvent(event: ChartEvents, data: any = {}) {
        this.eventBus.publish(event, data)
    }

    getDrawingSettings(id: string): DrawingSettingGroup<any, any>[] {
        return this.drawingManager.getDrawingSettings(id)
    }

    getDrawingMiniSettings(id: string): string[] {
        return this.drawingManager.getDrawingMiniSettings(id)
    }

    getSettingsValues(id: string): any {
        return this.drawingManager.getSettingsValues(id)
    }

    removeDrawing(id: string) {
        this.drawingManager.removeDrawing(id)
    }

    onBlur() {
        this.setFocused(false)
        this.drawingManager.deactivateAllDrawings()
    }

    syncDrawing(id: string) {
        this.drawingManager.syncDrawing(id)
    }

    unsyncDrawing(id: string) {
        this.drawingManager.unsyncDrawing(id)
    }

    updateDrawingSettings(id: string, settings: Record<string, any>) {
        this.drawingManager.updateDrawingSettings(id, settings)
    }

    updateChartDrawings(drawings: ChartDrawingItem[], drawingAdded: boolean | null) {
        // Series recreate calls removeAllDrawingsForSeries → updateChartDrawings([]) before
        // INITIAL applyAllDrawings. Preserve constructor/layout drawings until then.
        if (!this.initialDrawingsApplied && drawings.length === 0 && this.drawings.length > 0) {
            return
        }
        this.drawings = drawings
        this.callbacks.onDrawingsUpdate(drawings, drawingAdded ? drawingAdded : undefined)
    }

    publishDrawingEvent = (event: DrawingEvents, data: any, forced = false) => {
        this.callbacks.drawingsEventListener && this.callbacks.drawingsEventListener(event, data, forced)
    }

    setReplaySelectionTime(time: number, price: number, temporary = true) {
        const replayDrawing = this.drawingManager.getDrawing('replaySelection')

        if (!replayDrawing) return

        replayDrawing.updateState({
            time,
            price
        })
    }

    getCurrentReplaySelectionTime() {
        const replayDrawing = this.drawingManager.getDrawing('replaySelection')

        if (!replayDrawing) return 0
        return replayDrawing._state.time as number
    }

    onReplaySelectionMove(time: number, price: number) {
        this.callbacks.onReplaySelectionMove && this.callbacks.onReplaySelectionMove(time, price)
    }

    getChartApi(): IChartApi {
        return this.chartApi
    }

    destroy() {
        if (this.destroyed) return
        this.destroyed = true
        this.initialized = false
        this.overlayPriceScalePrimitive = null
        this.countdownPrimitive = null
        this.removeHighLowLines()
        this.removeDayHighLowLines()
        this.removePrevDayCloseLine()
        try {
            this.resizeObserver.unobserve(this.container)
            this.resizeObserver.disconnect()
        } catch {
            /* container may be detached */
        }
        this.drawingManager.destroy()
        this.dataManager.destroy()
        try {
            this.container.removeEventListener('mousedown', this.onMouseDown)
            if (this.touchEnabled) {
                this.container.removeEventListener('touchstart', this.onTouchChartHoverStart)
            }
        } catch {
            /* container may be detached */
        }
        document.removeEventListener('mousemove', this.onMouseMove)
        try {
            this.chartApi?.unsubscribeCrosshairMove(this.onCrosshairMove)
            this.chartApi?.unsubscribeClick(this.onChartClick)
            this.chartApi?.timeScale?.()?.unsubscribeVisibleTimeRangeChange(this.onVisibleTimeRangeChange)
            this.chartApi?.remove()
        } catch {
            /* chart may already be disposed */
        }
        ; (this as any).chartApi = null
    }
}
